import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsEnvelopeService, getReviewPrefill, getRecipientPrefill } from '@services/dsEnvelopeService';
import { dsCrossOrgSnapshotService } from '@services/dsCrossOrgSnapshotService';
import { getEmployeeById } from '@services/employeeService';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getTenantById } from '@services/tenantService';
import type { DocumentSnapshot } from '@workpay/ds-shared/types/snapshot';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { TemplatePickerModal } from './TemplatePickerModal';
import Modal from '@components/ui/Modal/Modal';
import SendForReviewModal from '@features/document-reviews/SendForReviewModal';
import { TextInput } from '@components/ui/Input';
import DatePicker from '@components/ui/DatePicker/DatePicker';
import { dsFileService } from '@services/dsFileService';
import { TemplateBuilder } from '@workpay/ds-shared/components/TemplateBuilder';
import FullPageStepper from '@components/shared/FullPageStepper/FullPageStepper';
import type { FullPageStepperStep } from '@components/shared/FullPageStepper/FullPageStepper';
import StepperModalFooter from '@components/shared/StepperModalFooter/StepperModalFooter';
import useMultiStepForm from '@hooks/useMultiStepForm';
import { Button } from '@components/ui';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { useNotificationStore } from '@stores/notificationStore';
import type {
  Envelope,
  EnvelopeDocument,
  EnvelopeRecipient,
  EnvelopeField,
  CreateEnvelopeRequest,
  EnvelopeDataValue,
  SigningMethod,
  RecipientRole,
  RecipientType,
} from '@workpay/ds-shared/types/envelope';
import type { DocumentTemplate, TemplateSigningField, TemplatePlaceholder } from '@workpay/ds-shared/types/template';
import { useSeedAuthorDefaults } from '@workpay/ds-shared/hooks/useSeedAuthorDefaults';
import { PrefilledAuthorFieldInput } from './PrefilledAuthorFieldInput';
import { EmailTagsInput } from './EmailTagsInput';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import type { MultiSelectOption } from '@workpay/ds-shared/components/ui/MultiSelect';
import styles from './SigningRequestWizard.module.scss';

interface SigningRequestWizardProps {
  routeTo: string;
  /**
   * Legacy prop — synonymous with `requestId`. Still accepted for backwards
   * compatibility with existing callers that used the pre-edit-flow wizard.
   */
  editRequestId?: string;
  /**
   * 'create' — fresh signing request (default).
   * 'edit'   — editing a SENT/in-progress signing request → uses editAndRetrigger on save.
   * 'continue' — resuming a DRAFT signing request → uses update + send.
   *
   * Full flow parity with document-signing is tracked under PLAN-envelope-migration §2.3.
   */
  mode?: 'create' | 'edit' | 'continue';
  /** Explicit requestId (preferred over legacy `editRequestId`). */
  requestId?: string;
  /** Pre-fetched signing request so the wizard doesn't need to refetch. */
  initialData?: Envelope;
  /** Retrigger semantics for edit mode. 'reset_all' discards all signatures; 'continue' keeps signed recipients. */
  retriggerMode?: 'reset_all' | 'continue';
  /** Jump directly to a given step (0-indexed) on mount — e.g. "Continue Editing" on a DRAFT lands on Place Fields. */
  startStep?: number;
  /**
   * Employee context — when present, the wizard pre-fills recipient[0] with
   * the employee, pre-scopes the subject, and narrows the template picker to
   * APPROVED templates matching the employee's country / legal entity /
   * employment type classification.
   */
  employeeId?: string;
}

interface WizardDocument {
  /** Envelope-document row id — only present in edit/continue mode after hydration. Used to map `envelope_placeholder.envelopeDocumentId` rows back to their wizard document. */
  id?: string;
  file?: File;
  name: string;
  documentOrder: number;
  sourceFileId: string;
  pageCount: number;
  sourceContentType?: string;
  /** Snapshot / master-template id — required by contract-service classification check. */
  sourceTemplateId?: string;
}

interface WizardRecipient {
  id: string;
  /**
   * Backend recipient UUID — set only for recipients hydrated from an existing
   * envelope (edit / continue / revert flows). When present, it flows into
   * `EnvelopeRecipient.id` on the payload so `editAndRetrigger` can match the
   * existing recipient row and dispatch the email correctly. Undefined for
   * recipients added inside the wizard — backend creates a fresh row.
   */
  persistedId?: string;
  name: string;
  email: string;
  role: RecipientRole;
  signingMethod: SigningMethod;
  signingOrder: number;
  recipientType?: RecipientType;
  userId?: string;
  /** 1-based template signer-slot numbers owned by this recipient. Source of truth for signer→recipient binding. */
  signerSlots: number[];
}

/** Treats a doc as DOCX when either the MIME type or filename declares it. */
function isDocxDoc(d: WizardDocument): boolean {
  return (
    d.sourceContentType?.includes('wordprocessingml') === true ||
    d.name?.toLowerCase().endsWith('.docx') === true
  );
}

/**
 * Stable signature of the documents array for change detection across the
 * Recipients & Signers → Fill Placeholders auto-save. Covers add / remove / reorder / template-swap.
 * Ad-hoc DOCX uploads are what actually need a server-side re-scan, but
 * fingerprinting the whole list is cheap + correct for edge cases (e.g. PDF
 * template added alongside ad-hoc DOCX).
 */
function buildDocsSig(docs: WizardDocument[]): string {
  return [...docs]
    .sort((a, b) => a.documentOrder - b.documentOrder)
    .map((d) => `${d.documentOrder}:${d.sourceFileId}:${d.sourceTemplateId ?? ''}`)
    .join('|');
}

/**
 * Stable fingerprint of the user's typed placeholder values. Drives the
 * Fill Placeholders → Send for Review auto-save gate — when the fingerprint
 * hasn't changed since the last successful `PUT /envelopes/{id}/placeholder-values`,
 * skip the call.
 */
function buildPlaceholderValuesSig(map: Record<string, string>): string {
  return Object.keys(map)
    .sort()
    .map((k) => `${k}=${map[k] ?? ''}`)
    .join('|');
}

function isoDateToBackendFormat(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
}

function backendDateToIso(value: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : value;
}

function buildRecipientsSig(recipients: WizardRecipient[], cc: string[], bcc: string[]): string {
  const recipPart = [...recipients]
    .sort((a, b) => a.signingOrder - b.signingOrder)
    .map(
      (r) =>
        `${r.email.trim().toLowerCase()}:${r.name.trim().toLowerCase()}:${r.role}:${r.signingMethod}:${r.signingOrder}:${r.recipientType ?? 'EXTERNAL'}:${[...r.signerSlots].sort((a, b) => a - b).join(',')}`,
    )
    .join('|');
  const ccPart = [...cc].map((e) => e.trim().toLowerCase()).sort().join(',');
  const bccPart = [...bcc].map((e) => e.trim().toLowerCase()).sort().join(',');
  return `${recipPart}#cc:${ccPart}#bcc:${bccPart}`;
}

export function SigningRequestWizard({
  routeTo,
  editRequestId,
  mode = 'create',
  requestId: requestIdProp,
  initialData,
  retriggerMode = 'reset_all',
  startStep,
  employeeId,
}: SigningRequestWizardProps) {
  // TODO(envelope-migration §2.3): full state-shape + dynamic step skipping +
  // PDF regeneration + author-prefilled rendering + mutation wrapping from
  // document-signing/src/features/envelope-wizard/EnvelopeWizard.tsx has not
  // landed yet. Until then, `mode`, `retriggerMode`, `startStep`, and
  // `initialData` are accepted but only `editRequestId` / `requestIdProp`
  // shape the behaviour — the wizard treats edit/continue the same as the
  // existing edit path. See PLAN-envelope-migration.md for the full refactor.
  const effectiveRequestId = requestIdProp ?? editRequestId;
  void mode;
  void retriggerMode;
  void initialData;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { countries } = useCountries();
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // Stepper-back warning state for the REVIEW_COMPLETED entry path. Set
  // when the user clicks Previous from Step 4; cleared when they cancel.
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  // Once the user accepts the warning + the revert mutation succeeds, this
  // flips to true and Steps 0–3 become visible (see `isStepVisible`).
  const [hasUserSteppedBack, setHasUserSteppedBack] = useState(false);

  // ---- Wizard state ----
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [documents, setDocuments] = useState<WizardDocument[]>([]);
  const [recipients, setRecipients] = useState<WizardRecipient[]>([
    {
      id: crypto.randomUUID(),
      name: '',
      email: '',
      role: 'SIGNER',
      signingMethod: 'ELECTRONIC',
      signingOrder: 1,
      recipientType: 'EXTERNAL',
      signerSlots: [],
    },
  ]);
  // Notification-fanout email lists — see FEATURE.md.
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  // Expiry — stored as `YYYY-MM-DD` (matches the `<input type="date">` contract
  // used in document-signing). Converted to an ISO `T23:59:59Z` timestamp at
  // payload-build time. Seeded from initialData in edit mode so we never
  // silently drop an existing expiry.
  const [expiresAt, setExpiresAt] = useState<string>(() =>
    initialData?.expiresAt ? initialData.expiresAt.substring(0, 10) : '',
  );
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [placeholderValues, setPlaceholderValues] = useState<EnvelopeDataValue[]>([]);
  const [signingFields, setSigningFields] = useState<TemplateSigningField[]>([]);
  // Per-document snapshot metadata (documentOrder → DocumentSnapshot). Populated
  // by handleTemplatesPicked; drives per-doc PDF regeneration + placeholder grouping.
  // Uses DocumentSnapshot (tenant-scoped approved copy), not the workpay master
  // DocumentTemplate — the picker surface now queries cross-org snapshots so
  // every field + placeholder + file id already belongs to the tenant.
  const [templateDetailsByDocOrder, setTemplateDetailsByDocOrder] = useState<
    Record<number, DocumentSnapshot>
  >({});
  // Union of TemplatePlaceholders across all chosen templates — tagged with
  // documentOrder + documentName so Step 3 can group by doc.
  const [templatePlaceholders, setTemplatePlaceholders] = useState<
    Array<TemplatePlaceholder & { documentOrder?: number; documentName?: string }>
  >([]);
  const templatePlaceholdersRef = useRef(templatePlaceholders);
  useEffect(() => {
    templatePlaceholdersRef.current = templatePlaceholders;
  }, [templatePlaceholders]);
  // Map of placeholderKey → current value typed by the user. Separate from the
  // EnvelopeDataValue[] returned by /resolve-placeholders (which is only wired
  // up in edit mode today); merged in buildSigningRequestPayload.
  const [placeholderValueMap, setPlaceholderValueMap] = useState<Record<string, string>>({});
  // AUTHOR-filled field values keyed by stable composite (field.id ??
  // `${docIndex}:${fieldOrder}`). Seeded from template defaults via
  // useSeedAuthorDefaults.
  const [prefilledFieldValues, setPrefilledFieldValues] = useState<Record<string, string>>({});
  const [pdfRegenerating, setPdfRegenerating] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<string | undefined>(effectiveRequestId);
  // Blob URLs keyed by documentOrder (1-based). Populated by handleTemplatesPicked
  // (preview for DOCX templates, source download for PDF templates) and
  // handleFileUpload (convertToPdf for DOCX, direct blob for PDF uploads).
  const [docPdfUrls, setDocPdfUrls] = useState<Record<number, string>>({});
  // Mirror ref so the on-unmount cleanup effect can read the latest without
  // a stale closure — matches document-signing's docPdfUrlsRef pattern.
  const docPdfUrlsRef = useRef<Record<number, string>>({});
  useEffect(() => {
    docPdfUrlsRef.current = docPdfUrls;
  }, [docPdfUrls]);
  // Cleanup on final unmount — revokes every blob to avoid memory leaks after
  // the wizard tears down. Empty dep array is intentional; we must NOT revoke
  // on every change (that'd kill URLs still in use).
  useEffect(() => {
    return () => {
      Object.values(docPdfUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);
  // Currently-active document for Step 5's TemplateBuilder (multi-doc nav).
  const [activeDocumentOrder, setActiveDocumentOrder] = useState(1);
  // Track the next documentOrder so batches don't collide with what's already
  // in `documents` (handleTemplatesPicked + handleFileUpload share this).
  const nextDocOrderRef = useRef(1);
  useEffect(() => {
    nextDocOrderRef.current = documents.length + 1;
  }, [documents.length]);
  // Records the `buildDocsSig(documents)` value at the last successful
  // auto-save during the Recipients & Signers → Fill Placeholders transition.
  // Used as a change gate so we skip the save (and the backend scanner)
  // when nothing about the doc list has changed since last time. See onValidateStep(1).
  const lastSavedDocsSigRef = useRef<string>('');
  const lastSavedRecipientsSigRef = useRef<string>('');
  // Parallel gate for Step 3's placeholder-values auto-save. Seeded on edit-mode
  // hydration so we don't fire a redundant PUT when the user just opens and
  // advances; updated after each successful save in onValidateStep(3).
  const lastSavedPlaceholderSigRef = useRef<string>('');
  const [isAdvancing, setIsAdvancing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Load existing signing request for edit ----
  const { data: existingSigningRequest, error: existingError } = useQuery({
    queryKey: ['dsEnvelope', effectiveRequestId],
    queryFn: () => dsEnvelopeService.getById(effectiveRequestId!),
    enabled: !!effectiveRequestId && !initialData,
    initialData,
  });

  // Resolve the entry-time envelope status from whichever source is available
  // synchronously. We deliberately read once and don't recompute when the
  // cached envelope flips status mid-session — once the user is in the wizard,
  // the wizard owns the flow until they save / cancel. Backend SSE invalidation
  // will refresh the cache for the *next* mount.
  const initialEnvelopeStatus = initialData?.status ?? existingSigningRequest?.status;
  // Active when the user landed on the wizard from a REVIEW_COMPLETED envelope
  // and hasn't yet accepted the back-button warning. Steps 0–3 are hidden in
  // this state so the user is forced through the explicit revert flow.
  const isReviewCompletedEntry =
    initialEnvelopeStatus === 'REVIEW_COMPLETED' && !hasUserSteppedBack;

  // ---- Load employee context when wizard is launched from "Generate Contract" ----
  const { data: employee, error: employeeError } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployeeById(employeeId!),
    enabled: !!employeeId && !effectiveRequestId,
  });

  // 403 on either of the wizard's load-bearing primaries (the envelope being
  // edited OR the employee whose contract is being generated) means the wizard
  // can't function — surface as a page-level Forbidden with the existing
  // close-and-navigate-back handler. Other queries below (employmentTypes,
  // reviewPrefill, recipientPrefill, editTemplateFieldsBySource) are partial
  // degradations and stay silent on 403 by design.
  const wizardForbidden = isForbiddenError(existingError) || isForbiddenError(employeeError);

  // `employee.tenantId` is the tenant-record id, not the organization id. The
  // cross-org snapshot search filters on `organizationId`, which lives on
  // `TenantDto.organizationId` — so resolve via the tenant, then pass the org id.
  const { data: employeeTenant } = useQuery({
    queryKey: ['tenant', employee?.tenantId],
    queryFn: () => getTenantById(employee!.tenantId),
    enabled: !!employee?.tenantId,
  });

  // Resolve employee's employment type name for the header chip (V3: backend returns employmentTypeId only).
  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', 'all'],
    queryFn: () => getEmploymentTypes({ size: 500 }),
    enabled: !!employee?.employmentTypeId,
  });

  // Backend-composed Send-for-Review prefill — target employee + union of
  // default reviewers across every source template on this envelope, deduped
  // on lower(email). Envelope-id driven, so the query stays disabled until the
  // first auto-save creates the draft envelope (Step 1 → Step 2).
  const { data: reviewPrefill } = useQuery({
    queryKey: ['envelopeReviewPrefill', createdRequestId],
    queryFn: () => getReviewPrefill(createdRequestId!),
    enabled: !!createdRequestId,
  });

  const { data: recipientPrefill } = useQuery({
    queryKey: ['envelopeRecipientPrefill', createdRequestId],
    queryFn: () => getRecipientPrefill(createdRequestId!),
    enabled: !!createdRequestId,
  });

  // Edit-mode template-field re-source. The envelope's `envelope_field` table
  // has no `recipient_index` column, so on read we get only `recipient_id`
  // (which is `NULL` for any slot the user hadn't mapped yet when they closed
  // the wizard mid-flight). Templates preserve the original slot number in
  // `recipientIndex` — refetch them on Continue Editing so hydration can use
  // them as the source of truth for slot identity. Falls back to envelope-only
  // hydration for ad-hoc DOCX uploads (no `sourceTemplateId`).
  const editTemplateSourceIds = useMemo(
    () =>
      Array.from(
        new Set(
          (existingSigningRequest?.documents ?? [])
            .map((d) => d.sourceTemplateId)
            .filter((id): id is string => !!id),
        ),
      ).sort(),
    [existingSigningRequest?.documents],
  );

  const { data: editTemplateFieldsData } = useQuery({
    queryKey: ['envelopeEditTemplateFields', editTemplateSourceIds.join(',')],
    queryFn: async () => {
      const map: Record<string, TemplateSigningField[]> = {};
      let hadForbidden = false;
      await Promise.all(
        editTemplateSourceIds.map(async (id) => {
          try {
            map[id] = await dsCrossOrgSnapshotService.getSigningFields(id);
          } catch (err) {
            // Silent fallback to envelope-only hydration. Track 403 separately
            // so the wizard can warn the user before they send a possibly
            // mis-mapped envelope (slot identity is recovered from templates).
            if (isForbiddenError(err)) hadForbidden = true;
          }
        }),
      );
      return { map, hadForbidden };
    },
    enabled:
      (mode === 'edit' || mode === 'continue') && editTemplateSourceIds.length > 0,
    staleTime: Infinity,
  });
  const editTemplateFieldsBySource = editTemplateFieldsData?.map;

  // Toast once per wizard mount if any template-fields fetch 403'd. Recipient
  // slot mapping may be wrong for slots that were unmapped at close-time —
  // user should verify the recipients screen before sending.
  const editTemplateForbiddenToastedRef = useRef(false);
  useEffect(() => {
    if (editTemplateFieldsData?.hadForbidden && !editTemplateForbiddenToastedRef.current) {
      editTemplateForbiddenToastedRef.current = true;
      addNotification({
        variant: 'critical',
        title: t(
          'K_DS_NOTIF_TEMPLATE_FIELDS_PARTIAL',
          "Some template data couldn't be loaded. Verify recipient assignments before sending.",
        ),
      });
    }
  }, [editTemplateFieldsData?.hadForbidden, addNotification, t]);

  // One-shot prefill seed — see FEATURE.md.
  const didApplyRecipientPrefillRef = useRef(false);
  useEffect(() => {
    if (didApplyRecipientPrefillRef.current) return;
    if (!recipientPrefill) return;
    didApplyRecipientPrefillRef.current = true;
    if (effectiveRequestId) return; // Edit/continue mode is driven by hydration.

    setRecipients((prev) => {
      const incoming = recipientPrefill.signers.filter((s) => s.email);
      if (incoming.length === 0) return prev;

      const allPristine = prev.every(
        (r) => !r.name.trim() && !r.email.trim() && r.signerSlots.length === 0,
      );
      if (allPristine) {
        return incoming.map((s, idx) => ({
          id: crypto.randomUUID(),
          name: s.name ?? '',
          email: s.email!,
          role: 'SIGNER' as const,
          signingMethod: 'ELECTRONIC' as const,
          signingOrder: s.signingOrder ?? idx + 1,
          recipientType: 'EXTERNAL' as const,
          signerSlots: [],
        }));
      }

      // Append only prefill rows whose email isn't already present.
      const existing = new Set(
        prev.map((r) => r.email.trim().toLowerCase()).filter(Boolean),
      );
      const additions = incoming
        .filter((s) => !existing.has(s.email!.trim().toLowerCase()))
        .map((s, i) => ({
          id: crypto.randomUUID(),
          name: s.name ?? '',
          email: s.email!,
          role: 'SIGNER' as const,
          signingMethod: 'ELECTRONIC' as const,
          signingOrder: prev.length + i + 1,
          recipientType: 'EXTERNAL' as const,
          signerSlots: [],
        }));
      return additions.length === 0 ? prev : [...prev, ...additions];
    });

    setCcEmails((prev) =>
      prev.length === 0 && recipientPrefill.cc?.length ? [...recipientPrefill.cc] : prev,
    );
    setBccEmails((prev) =>
      prev.length === 0 && recipientPrefill.bcc?.length ? [...recipientPrefill.bcc] : prev,
    );
  }, [recipientPrefill, effectiveRequestId]);

  // Compose the initial reviewer list from the backend's flat `reviewers` array
  // (position-significant: index 0 is the employee, rest are tenant super_admins).
  // Filter rows missing an email (backend allows nulls; modal expects strings) and
  // dedupe by lowercased email so the employee isn't listed twice when they're
  // also a super_admin of the tenant.
  const initialReviewers = useMemo(() => {
    if (!reviewPrefill) return undefined;
    const seen = new Set<string>();
    const deduped: Array<{ name: string; email: string }> = [];
    for (const r of reviewPrefill.reviewers) {
      if (!r.email) continue;
      const key = r.email.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push({ name: r.name ?? r.email, email: r.email });
    }
    return deduped.length > 0 ? deduped : undefined;
  }, [reviewPrefill]);
  const employeeEmploymentTypeName = useMemo(() => {
    if (!employee?.employmentTypeId) return null;
    const match = (employmentTypesData?.content ?? []).find((et) => et.id === employee.employmentTypeId);
    return match?.name ?? null;
  }, [employee?.employmentTypeId, employmentTypesData]);

  // Template picker filter derived from the employee's tenant + classification tuple.
  // Stays stable across renders so the picker modal's useQuery key is stable.
  const employeeContext = useMemo(
    () =>
      employee
        ? {
            // `organizationId` = tenant's organization id — read from the nested
            // `tenant.organization.id` (the expanded org object), NOT from
            // `tenant.tenant.organizationId` which was returning the tenant
            // record id in practice (visible in the request payload: we saw
            // 1b46ac11-… flowing through, which matches tenant.id, not the
            // expected 8da6518b-… on tenant.organization.id).
            organizationId: employeeTenant?.organization?.id,
            countryId: employee.countryId,
            legalEntityId: employee.legalEntityId ?? undefined,
            employmentTypeId: (employee as unknown as { employmentTypeId?: string })
              .employmentTypeId,
          }
        : undefined,
    [employee, employeeTenant],
  );

  // Pre-fill recipient[0] with the employee once loaded (create mode only).
  const didSeedRecipientRef = useRef(false);
  useEffect(() => {
    if (didSeedRecipientRef.current) return;
    if (!employee || effectiveRequestId) return;
    const name = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim();
    const email = (employee as unknown as { workEmail?: string; email?: string }).workEmail
      ?? (employee as unknown as { email?: string }).email
      ?? '';
    // Always seed the employee recipient as EXTERNAL — contract-service emails
    // a token-based signing link. We intentionally don't map `employee.userId`
    // to an INTERNAL platform-user binding: the employee may not have logged in
    // yet (INACTIVE userId) and shouldn't be forced through session auth.
    setRecipients((prev) => {
      const first = prev[0];
      if (!first) return prev;
      return [
        {
          ...first,
          name: first.name || name,
          email: first.email || email,
          recipientType: 'EXTERNAL' as RecipientType,
          userId: undefined,
        },
        ...prev.slice(1),
      ];
    });
    didSeedRecipientRef.current = true;
  }, [employee, effectiveRequestId]);
  void employeeContext;

  useEffect(() => {
    if (!existingSigningRequest) return;
    // Wait for the template-fields re-source query before hydrating when the
    // envelope has docs that originate from a template. Without template fields
    // we can't recover the original slot identity of unmapped envelope fields
    // (the backend's `envelope_field` table has no `recipient_index` column),
    // and the synthesized `slot = recipientIdx + 1` fallback collapses every
    // unmapped field onto recipient[0] / slot 1.
    const hasTemplateDocs = (existingSigningRequest.documents ?? []).some(
      (d) => d.sourceTemplateId,
    );
    if (hasTemplateDocs && (mode === 'edit' || mode === 'continue')
        && !editTemplateFieldsBySource) {
      return;
    }
    setSubject(existingSigningRequest.subject);
    setMessage(existingSigningRequest.message ?? '');
    setExpiresAt(
      existingSigningRequest.expiresAt
        ? existingSigningRequest.expiresAt.substring(0, 10)
        : '',
    );
    setCcEmails(existingSigningRequest.cc ?? []);
    setBccEmails(existingSigningRequest.bcc ?? []);
    setDocuments(existingSigningRequest.documents.map((d) => ({
      id: d.id,
      name: d.name,
      documentOrder: d.documentOrder,
      sourceFileId: d.sourceFileId,
      pageCount: d.pageCount,
      sourceContentType: d.sourceContentType,
      sourceTemplateId: d.sourceTemplateId,
    })));
    // Backend stores `recipientId`/`documentId` (UUIDs) as the source of truth
    // and does NOT consistently round-trip the `recipientIndex`/`documentIndex`
    // numeric positions. Build UUID → index lookups once, then use them to
    // recover the indices the wizard needs.
    const recipientIdToIdx: Record<string, number> = {};
    existingSigningRequest.recipients.forEach((r, idx) => {
      if (r.id) recipientIdToIdx[r.id] = idx;
    });
    const docIdToOrder: Record<string, number> = {};
    for (const d of existingSigningRequest.documents ?? []) {
      if (d.id) docIdToOrder[d.id] = d.documentOrder;
    }
    const sourceTemplateIdByDocOrder: Record<number, string> = {};
    for (const d of existingSigningRequest.documents ?? []) {
      if (d.sourceTemplateId) sourceTemplateIdByDocOrder[d.documentOrder] = d.sourceTemplateId;
    }

    // Look up a field's original template-defined slot. `envelope_field`
    // doesn't store slot identity, so we recover it from the source template
    // via `(documentOrder, fieldOrder)`. Returns null for AUTHOR fields,
    // ad-hoc DOCX docs (no sourceTemplateId), and any failed template fetch.
    const lookupTemplateSlot = (envField: {
      documentId?: string;
      documentIndex?: number;
      fieldOrder?: number;
      filledBy?: string;
    }): number | null => {
      if (envField.filledBy === 'AUTHOR') return null;
      if (!editTemplateFieldsBySource) return null;
      const docOrder = envField.documentId
        ? docIdToOrder[envField.documentId]
        : envField.documentIndex;
      if (docOrder == null) return null;
      const tplId = sourceTemplateIdByDocOrder[docOrder];
      if (!tplId) return null;
      const tplFields = editTemplateFieldsBySource[tplId];
      if (!tplFields) return null;
      const match = tplFields.find((tf) => tf.fieldOrder === envField.fieldOrder);
      if (!match || match.filledBy === 'AUTHOR') return null;
      return match.recipientIndex > 0 ? match.recipientIndex : null;
    };

    // Resolve each field's recipient (0-based idx). Priority:
    //   1. recipientId UUID lookup (canonical, post-mapping state)
    //   2. null = unmapped (don't default to recipient 0 — that's the bug
    //      that collapses every unmapped slot onto the first recipient).
    const fields = existingSigningRequest.fields ?? [];
    const slotsByRecipientIdx: Record<number, Set<number>> = {};
    const resolvedRecipientIdxByField: Array<number | null> = fields.map((f) => {
      if (f.filledBy === 'AUTHOR') return null;
      if (f.recipientId && recipientIdToIdx[f.recipientId] !== undefined) {
        return recipientIdToIdx[f.recipientId]!;
      }
      return null;
    });
    resolvedRecipientIdxByField.forEach((rIdx, fieldIdx) => {
      if (rIdx === null || rIdx === undefined) return;
      const fieldRow = fields[fieldIdx]!;
      // Prefer template-defined slot when available; fall back to recipient
      // position for ad-hoc DOCX (no template) where slot identity collapses
      // onto position by construction.
      const slot = lookupTemplateSlot(fieldRow) ?? (rIdx + 1);
      if (slot <= 0) return;
      if (!slotsByRecipientIdx[rIdx]) slotsByRecipientIdx[rIdx] = new Set();
      slotsByRecipientIdx[rIdx].add(slot);
    });

    // Migrate legacy role=CC rows into ccEmails — see FEATURE.md.
    const ccFromLegacy: string[] = [];
    const signerRowsWithOriginalIdx: Array<{
      r: (typeof existingSigningRequest.recipients)[number];
      originalIdx: number;
    }> = [];
    existingSigningRequest.recipients.forEach((r, originalIdx) => {
      if (r.role === 'CC') {
        if (r.email) ccFromLegacy.push(r.email);
        return;
      }
      signerRowsWithOriginalIdx.push({ r, originalIdx });
    });
    if (ccFromLegacy.length > 0) {
      setCcEmails((prev) => {
        const merged = [...prev];
        const seen = new Set(merged.map((e) => e.toLowerCase()));
        for (const e of ccFromLegacy) {
          if (!seen.has(e.toLowerCase())) {
            merged.push(e);
            seen.add(e.toLowerCase());
          }
        }
        return merged;
      });
    }

    setRecipients(
      signerRowsWithOriginalIdx.map(({ r, originalIdx }) => ({
        id: crypto.randomUUID(),
        // Preserve the backend recipient UUID so editAndRetrigger can match
        // this row server-side — without it the backend treats the recipient
        // as new and the resend email is never queued for the existing signer.
        persistedId: r.id,
        name: r.name,
        email: r.email,
        role: r.role,
        signingMethod: r.signingMethod,
        signingOrder: r.signingOrder,
        recipientType: r.recipientType ?? 'EXTERNAL',
        userId: r.userId,
        signerSlots: [...(slotsByRecipientIdx[originalIdx] ?? [])].sort((a, b) => a - b),
      })),
    );

    // Build hydrated `signingFields`. Strategy:
    //  1. Start from envelope-saved fields, but use the template's slot
    //     identity for `recipientIndex` so the dropdown surfaces every slot
    //     the template declares (even ones where the user hadn't yet picked
    //     a recipient before closing).
    //  2. For each template field that has NO matching envelope field (e.g.
    //     because Step 0 → 1 autosave skipped it under the save-side guard),
    //     append it from the template so the slot still appears in the
    //     dropdown. Positions/properties come from the template; the user
    //     re-places them in Step 4 (Place Fields) if needed.
    const envelopeFieldKey = (docOrder: number | undefined, fieldOrder: number | undefined) =>
      `${docOrder ?? 0}:${fieldOrder ?? 0}`;
    const envelopeFieldsKeys = new Set<string>();
    const hydrated: TemplateSigningField[] = (existingSigningRequest.fields ?? []).map(
      (f, i) => {
        const isAuthor = f.filledBy === 'AUTHOR';
        const resolvedRIdx = resolvedRecipientIdxByField[i];
        const templateSlot = lookupTemplateSlot(f);
        const safeRecipientIndex = isAuthor
          ? 0
          : templateSlot ?? (resolvedRIdx !== null && resolvedRIdx !== undefined
              ? resolvedRIdx + 1
              : 1);
        const incomingDocIdx = f.documentIndex;
        const docFromId = f.documentId ? docIdToOrder[f.documentId] : undefined;
        const safeDocIdx =
          typeof incomingDocIdx === 'number' && incomingDocIdx > 0
            ? incomingDocIdx
            : (docFromId ?? 1);
        envelopeFieldsKeys.add(envelopeFieldKey(safeDocIdx, f.fieldOrder));
        return {
          fieldType: f.fieldType as TemplateSigningField['fieldType'],
          label: f.label,
          recipientIndex: safeRecipientIndex,
          pageNumber: f.pageNumber,
          xPosition: f.xPosition,
          yPosition: f.yPosition,
          width: f.width,
          height: f.height,
          isRequired: f.isRequired,
          fontSize: f.fontSize,
          defaultValue: f.defaultValue,
          dropdownOptions: f.dropdownOptions,
          fieldOrder: f.fieldOrder,
          isReadOnly: f.isReadOnly,
          documentIndex: safeDocIdx,
          filledBy: f.filledBy,
        };
      },
    );

    if (editTemplateFieldsBySource) {
      for (const doc of existingSigningRequest.documents ?? []) {
        if (!doc.sourceTemplateId) continue;
        const tplFields = editTemplateFieldsBySource[doc.sourceTemplateId];
        if (!tplFields) continue;
        for (const tf of tplFields) {
          const key = envelopeFieldKey(doc.documentOrder, tf.fieldOrder);
          if (envelopeFieldsKeys.has(key)) continue;
          // Append template field as unmapped (user re-maps in Step 2 and
          // re-places in Step 4 if positions were never persisted).
          hydrated.push({
            ...tf,
            documentIndex: doc.documentOrder,
          });
        }
      }
    }

    setSigningFields(hydrated);
  }, [existingSigningRequest, editTemplateFieldsBySource, mode]);

  // Edit/continue mode — one-shot hydration on mount: fetch persisted
  // placeholder values, then for each doc fetch its snapshot + PDF blob.
  // Runs serially to avoid a double generate-pdf (once with empty values,
  // once with populated values). Guarded by a ref so StrictMode double-mount
  // doesn't refire.
  const didHydrateForEditRef = useRef(false);
  useEffect(() => {
    if (didHydrateForEditRef.current) return;
    if (!effectiveRequestId) return;
    if (!(mode === 'edit' || mode === 'continue')) return;
    if (documents.length === 0) return;
    didHydrateForEditRef.current = true;

    let cancelled = false;
    // Hoisted so the outer `.finally()` can read the hydrated values when
    // stamping `lastSavedPlaceholderSigRef` — async IIFE body has its own scope.
    let mergedPlaceholderValues: Record<string, string> = {};
    setPdfRegenerating(true);

    (async () => {
      // 1. Placeholder schema + values.
      //   - `listPlaceholders` returns the envelope-scoped SCHEMA (keys, labels,
      //     `fieldType`, per-doc origin). Backend auto-seeds on create:
      //     scanner-driven for ad-hoc DOCX, catalog-copied for template docs.
      //   - `getPlaceholderValues` returns what the user has typed (or what the
      //     server's `KnownPlaceholderResolver` auto-populated).
      //
      // Order: schema first, then values. We need the schema to know which keys
      // are DATE-typed so we can normalise backend's `dd/MM/yyyy` → ISO for
      // DatePicker before stamping into `placeholderValueMap`.
      const merged = mergedPlaceholderValues;

      const keyToFieldType = new Map<string, TemplatePlaceholder['fieldType']>();
      try {
        const schema = await dsEnvelopeService.listPlaceholders(effectiveRequestId);
        if (cancelled) return;
        for (const row of schema) {
          keyToFieldType.set(row.placeholderKey, row.fieldType);
        }
        if (schema.length > 0) {
          setTemplatePlaceholders((prev) => {
            const existingKeys = new Set(prev.map((p) => p.placeholderKey));
            const adapted: Array<TemplatePlaceholder & { documentOrder?: number; documentName?: string }> = [];
            for (const row of schema) {
              if (existingKeys.has(row.placeholderKey)) continue;
              existingKeys.add(row.placeholderKey);
              const docOrder = documents.find((d) => d.id === row.envelopeDocumentId)?.documentOrder;
              adapted.push({
                id: row.id,
                placeholderKey: row.placeholderKey,
                displayLabel: row.displayLabel ?? row.placeholderKey,
                fieldType: row.fieldType,
                isRequired: row.required,
                placeholderOrder: row.placeholderOrder,
                dataSourceType: (row.dataSourceType as TemplatePlaceholder['dataSourceType']) ?? 'CUSTOM',
                defaultValue: row.defaultValue,
                documentOrder: docOrder,
                documentName: documents.find((d) => d.id === row.envelopeDocumentId)?.name,
              });
            }
            return adapted.length > 0 ? [...prev, ...adapted] : prev;
          });
        }
      } catch {
        /* non-fatal — Step 3 falls back to template-sourced placeholders only */
      }

      try {
        const values = await dsEnvelopeService.getPlaceholderValues(effectiveRequestId);
        if (cancelled) return;
        if (values.length > 0) {
          setPlaceholderValues(values);
          setPlaceholderValueMap((prev) => {
            const next = { ...prev };
            for (const v of values) {
              if (next[v.placeholderKey] === undefined && v.resolvedValue) {
                const raw = v.resolvedValue;
                next[v.placeholderKey] =
                  keyToFieldType.get(v.placeholderKey) === 'DATE'
                    ? backendDateToIso(raw)
                    : raw;
              }
            }
            return next;
          });
          for (const v of values) {
            if (v.resolvedValue) {
              merged[v.placeholderKey] =
                keyToFieldType.get(v.placeholderKey) === 'DATE'
                  ? backendDateToIso(v.resolvedValue)
                  : v.resolvedValue;
            }
          }
        }
      } catch {
        /* non-fatal */
      }

      // Stable signature for the merged map — stamped into `lastGenSigRef`
      // below so the Step 5 regen effect treats this hydration as satisfying
      // its own "latest generation" bookkeeping and doesn't refire.
      const sig = Object.keys(merged)
        .sort()
        .map((k) => `${k}=${merged[k]}`)
        .join('|');

      // 2. Per-doc snapshot + PDF.
      // PDF templates → direct download. DOCX templates → generatePdf.
      await Promise.all(
        documents
          .filter((d) => d.sourceTemplateId)
          .map(async (doc) => {
            try {
              const snap = await dsCrossOrgSnapshotService.getById(doc.sourceTemplateId!);
              if (cancelled) return;
              setTemplateDetailsByDocOrder((prev) => ({ ...prev, [doc.documentOrder]: snap }));
              if (snap.templateFormat === 'DOCX') {
                lastGenSigRef.current[doc.documentOrder] = `${snap.id}:${sig}`;
              }
              const res =
                snap.templateFormat === 'PDF'
                  ? await dsFileService.download(snap.sourceFileId)
                  : await dsCrossOrgSnapshotService.generatePdf(snap.id, merged);
              if (cancelled || !res.data || res.data.size === 0) return;
              const url = URL.createObjectURL(res.data);
              setDocPdfUrls((prev) => ({ ...prev, [doc.documentOrder]: url }));
            } catch {
              /* per-doc failure non-fatal; Step 5 falls back to empty state */
            }
          }),
      );
    })().finally(() => {
      if (cancelled) return;
      lastSavedDocsSigRef.current = buildDocsSig(documents);
      lastSavedRecipientsSigRef.current = buildRecipientsSig(recipients, ccEmails, bccEmails);
      lastSavedPlaceholderSigRef.current = buildPlaceholderValuesSig(mergedPlaceholderValues);
      setPdfRegenerating(false);
    });

    return () => {
      cancelled = true;
    };
  }, [effectiveRequestId, mode, documents]);

  // (Template picking happens exclusively through the TemplatePickerModal —
  // employee-scoped filter, multi-select. The old "browse all ACTIVE templates"
  // grid was removed; no flat template list is needed here.)

  // ---- Steps ----
  const steps = useMemo<FullPageStepperStep[]>(() => [
    {
      id: 'documents',
      label: t('K_DS_STEP_ADD_DOCS', 'Add Documents'),
      title: t('K_DS_STEP_ADD_DOCS_TITLE', 'Add Documents'),
      description: t('K_DS_STEP_ADD_DOCS_DESC', 'Upload PDF/DOCX files or select from templates.'),
    },
    {
      id: 'recipients',
      label: t('K_DS_STEP_RECIPIENTS_AND_SIGNERS', 'Recipients & Signers'),
      title: t('K_DS_STEP_RECIPIENTS_AND_SIGNERS_TITLE', 'Add Recipients & Map Signers'),
      description: t(
        'K_DS_STEP_RECIPIENTS_AND_SIGNERS_DESC',
        'Add signers and assign them to template signer slots.',
      ),
    },
    {
      id: 'placeholders',
      label: t('K_DS_STEP_PLACEHOLDERS', 'Fill Placeholders'),
      title: t('K_DS_STEP_PLACEHOLDERS_TITLE', 'Fill Data Placeholders'),
      description: t('K_DS_STEP_PLACEHOLDERS_DESC', 'Provide values for document placeholders.'),
    },
    {
      id: 'review',
      label: t('K_DS_STEP_REVIEW', 'Review'),
      title: t('K_DS_STEP_REVIEW_TITLE', 'Review Documents'),
      description: t('K_DS_STEP_REVIEW_DESC', 'Preview your documents before placing fields.'),
    },
    {
      id: 'placeFields',
      label: t('K_DS_STEP_PLACE_FIELDS', 'Place Fields'),
      title: t('K_DS_STEP_PLACE_FIELDS_TITLE', 'Place Signing Fields'),
      description: t('K_DS_STEP_PLACE_FIELDS_DESC', 'Drag and drop signing fields onto the documents.'),
    },
    {
      id: 'send',
      label: t('K_DS_STEP_SEND', 'Review & Send'),
      title: t('K_DS_STEP_SEND_TITLE', 'Review & Send'),
      description: t('K_DS_STEP_SEND_DESC', 'Review the final summary and send the signing request.'),
    },
  ], [t]);

  const resolvedStartStep = startStep ?? 0;

  const { currentStep, isFirstStep, isLastStep, goToNext, goToPrev, goToStep, setStepDirect, direction } = useMultiStepForm({
    totalSteps: steps.length,
    initialStep: resolvedStartStep,
    onValidateStep: async (stepIndex) => {
      const autoSave = async (context: string): Promise<boolean> => {
        try {
          await persistDraft();
          return true;
        } catch (err) {
          addNotification({
            variant: 'critical',
            title: t('K_DS_NOTIF_SIGNING_REQUEST_SAVE_FAILED', 'Failed to save signing request'),
            description: err instanceof Error ? `${context}: ${err.message}` : context,
          });
          return false;
        }
      };

      if (stepIndex === 0) {
        if (documents.length === 0 && !selectedTemplate) {
          addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_ADD_DOCUMENT', 'Please add at least one document or select a template.') });
          return false;
        }
        if (!subject.trim()) {
          addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_SUBJECT_REQUIRED', 'Please enter a subject.') });
          return false;
        }
        // Create draft early so recipient-prefill can fire on Step 1 entry.
        if (!createdRequestId && employeeId) {
          return await autoSave('Step 0 → 1');
        }
        return true;
      }
      if (stepIndex === 1) {
        // Recipient basics — at least one filled row + valid emails.
        const validRecipients = recipients.filter((r) => r.name.trim() && r.email.trim());
        if (validRecipients.length === 0) {
          addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_ADD_RECIPIENT', 'Please add at least one recipient with name and email.') });
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidSigners = recipients
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => r.email.trim())
          .filter(({ r }) => !emailRegex.test(r.email.trim()))
          .map(({ i }) => `#${i + 1}`);
        if (invalidSigners.length > 0) {
          addNotification({
            variant: 'critical',
            title: t('K_DS_VALIDATION_RECIPIENT_EMAIL_INVALID', 'Invalid recipient email'),
            description: invalidSigners.join(', '),
          });
          return false;
        }

        const ccTrimmed = ccEmails.map((e) => e.trim()).filter(Boolean);
        const bccTrimmed = bccEmails.map((e) => e.trim()).filter(Boolean);

        const invalidCc = ccTrimmed.filter((e) => !emailRegex.test(e));
        if (invalidCc.length > 0) {
          addNotification({
            variant: 'critical',
            title: t('K_DS_VALIDATION_INVALID_CC_EMAIL', 'One or more CC entries are not valid emails.'),
            description: invalidCc.join(', '),
          });
          return false;
        }

        const invalidBcc = bccTrimmed.filter((e) => !emailRegex.test(e));
        if (invalidBcc.length > 0) {
          addNotification({
            variant: 'critical',
            title: t('K_DS_VALIDATION_INVALID_BCC_EMAIL', 'One or more BCC entries are not valid emails.'),
            description: invalidBcc.join(', '),
          });
          return false;
        }

        // Cross-section duplicate check — see FEATURE.md. Per-section sets so
        // an intra-section dup ([a@b, a@b] in one list) doesn't trigger this
        // toast (payload-build dedupes those silently).
        const signerSet = new Set(
          recipients.map((r) => r.email.trim().toLowerCase()).filter(Boolean),
        );
        const ccSet = new Set(ccTrimmed.map((e) => e.toLowerCase()));
        const bccSet = new Set(bccTrimmed.map((e) => e.toLowerCase()));
        const crossDupes = new Set<string>();
        for (const e of ccSet) if (signerSet.has(e)) crossDupes.add(e);
        for (const e of bccSet) if (signerSet.has(e)) crossDupes.add(e);
        for (const e of bccSet) if (ccSet.has(e)) crossDupes.add(e);
        if (crossDupes.size > 0) {
          addNotification({
            variant: 'critical',
            title: t(
              'K_DS_VALIDATION_DUPLICATE_EMAIL',
              'An email cannot appear in more than one section (Signers / CC / BCC).',
            ),
            description: [...crossDupes].join(', '),
          });
          return false;
        }

        // Signer-mapping completeness. Every non-AUTHOR slot the template
        // declares must be owned by exactly one recipient. Payload build
        // substitutes signingFields[].recipientIndex from derivedSignerMappings,
        // so unmapped slots would send the wrong person a signing link.
        if (allSignerSlots.length > 0) {
          const seen = new Set<number>();
          const duplicates = new Set<number>();
          for (const r of recipients) {
            for (const slot of r.signerSlots) {
              if (seen.has(slot)) duplicates.add(slot);
              seen.add(slot);
            }
          }
          const unmapped = allSignerSlots.filter((s) => !seen.has(s));
          if (unmapped.length > 0) {
            addNotification({
              variant: 'critical',
              title: t('K_DS_VALIDATION_MAP_SIGNERS', 'Please map every signer to a recipient.'),
              description: unmapped.map((s) => `Signer ${s}`).join(', '),
            });
            return false;
          }
          if (duplicates.size > 0) {
            addNotification({
              variant: 'critical',
              title: t(
                'K_DS_VALIDATION_MAP_SIGNERS_DUPLICATE',
                'A signer can only be assigned to one recipient.',
              ),
              description: [...duplicates].map((s) => `Signer ${s}`).join(', '),
            });
            return false;
          }
          if (recipients.some((r) => r.role === 'CC' && r.signerSlots.length > 0)) {
            addNotification({
              variant: 'critical',
              title: t(
                'K_DS_VALIDATION_CC_NO_SIGNER',
                'CC recipients cannot be assigned signers.',
              ),
            });
            return false;
          }
        }

        // Ad-hoc DOCX scan-on-save (merged from former Step 2). Backend's
        // DocxPlaceholderScanner only runs on POST/PUT envelope, so without
        // this the wizard's Step 2 (Fill Placeholders) would silently skip for
        // ad-hoc DOCX uploads.
        //
        // Trigger rules (AND):
        //  1. Doc list changed since the last save (sig diff).
        //  2. Either (a) at least one ad-hoc DOCX is present — the classic
        //     first-save case — or (b) a draft already exists. Case (b) covers
        //     the "user removed the only ad-hoc DOCX" path: without re-saving,
        //     templatePlaceholders would still hold the scanner-seeded rows for
        //     the removed doc. Every PUT re-seeds envelope_placeholder, so the
        //     replace below drops stale entries naturally.
        //  3. First-time entry on a pure-template flow does NOT trigger — we
        //     don't want to create a throwaway draft just because the user
        //     picked templates. That flow persists only on final submit.
        const hasAdhocDocx = documents.some(
          (d) => !d.sourceTemplateId && isDocxDoc(d),
        );
        const currentDocsSig = buildDocsSig(documents);
        const sigChanged = lastSavedDocsSigRef.current !== currentDocsSig;
        const needsScanSave = sigChanged && (hasAdhocDocx || !!createdRequestId);
        if (!needsScanSave) {
          return await autoSave('Step 1 → 2');
        }

        try {
          const saved = await persistDraft();
          if (!saved) return true;

          const [schema, values] = await Promise.all([
            dsEnvelopeService.listPlaceholders(saved.id),
            dsEnvelopeService.getPlaceholderValues(saved.id).catch(() => [] as EnvelopeDataValue[]),
          ]);

          const savedDocs = saved.documents ?? [];

          const seen = new Set<string>();
          const adapted: Array<
            TemplatePlaceholder & { documentOrder?: number; documentName?: string }
          > = [];
          for (const row of schema) {
            if (seen.has(row.placeholderKey)) continue;
            seen.add(row.placeholderKey);
            const ownerDoc = savedDocs.find((cd) => cd.id === row.envelopeDocumentId);
            adapted.push({
              id: row.id,
              placeholderKey: row.placeholderKey,
              displayLabel: row.displayLabel ?? row.placeholderKey,
              fieldType: row.fieldType,
              isRequired: row.required,
              placeholderOrder: row.placeholderOrder,
              dataSourceType:
                (row.dataSourceType as TemplatePlaceholder['dataSourceType']) ?? 'CUSTOM',
              defaultValue: row.defaultValue,
              documentOrder: ownerDoc?.documentOrder,
              documentName: ownerDoc?.name,
            });
          }
          setTemplatePlaceholders(adapted);
          setPlaceholderValues(values);
          const adaptedFieldTypes = new Map(
            adapted.map((p) => [p.placeholderKey, p.fieldType]),
          );
          setPlaceholderValueMap((prev) => {
            const next = { ...prev };
            for (const v of values) {
              if (next[v.placeholderKey] === undefined && v.resolvedValue) {
                next[v.placeholderKey] =
                  adaptedFieldTypes.get(v.placeholderKey) === 'DATE'
                    ? backendDateToIso(v.resolvedValue)
                    : v.resolvedValue;
              }
            }
            return next;
          });
          return true;
        } catch (err) {
          addNotification({
            variant: 'critical',
            title: t(
              'K_DS_NOTIF_PLACEHOLDER_SCAN_FAILED',
              'Failed to prepare placeholders',
            ),
            description: err instanceof Error ? err.message : undefined,
          });
          return false;
        }
      }
      if (stepIndex === 2) {
        // Match document-signing: block advance when any AUTHOR-prefilled field
        // marked `isRequired` is still empty. DOCX data placeholders are NOT
        // blocked here — they resolve server-side from data sources after draft
        // save (or via the deferred usePlaceholderResolver hook), so the backend
        // can still fill them even if the user leaves them blank in the UI.
        const missing = signingFields
          .filter((f) => f.filledBy === 'AUTHOR' && f.isRequired)
          .filter((f) => {
            const v = prefilledFieldValues[fieldKey(f)];
            return !v || !v.toString().trim();
          })
          .map((f) => f.label || f.fieldType);
        if (missing.length > 0) {
          addNotification({
            variant: 'critical',
            title: t(
              'K_DS_VALIDATION_PREFILL_REQUIRED',
              'Please fill all required prefilled fields',
            ),
            description: missing.join(', '),
          });
          return false;
        }

        // Persist DOCX placeholder values before leaving Fill Placeholders.
        if (!(await autoSave('Step 2 → 3'))) return false;

        if (Object.keys(placeholderValueMap).length === 0) return true;
        const currentSig = buildPlaceholderValuesSig(placeholderValueMap);
        if (lastSavedPlaceholderSigRef.current === currentSig) return true;

        try {
          const reqId = createdRequestId;
          if (!reqId) {
            return true;
          }
          const values: EnvelopeDataValue[] = Object.entries(placeholderValueMap).map(
            ([key, val]) => {
              const ph = templatePlaceholders.find((p) => p.placeholderKey === key);
              const resolvedValue =
                ph?.fieldType === 'DATE' ? isoDateToBackendFormat(val) : val;
              return { placeholderKey: key, resolvedValue } as EnvelopeDataValue;
            },
          );
          await dsEnvelopeService.updatePlaceholderValues(reqId, values);
          lastSavedPlaceholderSigRef.current = currentSig;
          return true;
        } catch (err) {
          addNotification({
            variant: 'critical',
            title: t(
              'K_DS_NOTIF_PLACEHOLDER_VALUES_SAVE_FAILED',
              'Failed to save placeholder values',
            ),
            description: err instanceof Error ? err.message : undefined,
          });
          return false;
        }
      }
      if (stepIndex === 3) {
        return await autoSave('Step 3 → 4');
      }
      if (stepIndex === 4) {
        // Block advance when a DROPDOWN field has no usable options — signers
        // cannot pick from an empty list and Finish Signing stays disabled,
        // blocking the entire envelope. Backend does not enforce this today.
        const invalidDropdowns = signingFields
          .filter((f) => f.fieldType === 'DROPDOWN')
          .filter((f) => !(f.dropdownOptions ?? '').split(',').some((o) => o.trim() !== ''))
          .map((f, i) => f.label || `Dropdown ${i + 1}`);
        if (invalidDropdowns.length > 0) {
          addNotification({
            variant: 'critical',
            title: t(
              'K_DS_VALIDATION_DROPDOWN_OPTIONS_REQUIRED',
              'Dropdown fields require at least one option',
            ),
            description: invalidDropdowns.join(', '),
          });
          return false;
        }
        return await autoSave('Step 4 → 5');
      }
      return true;
    },
  });

  // Stable key for a template signing field — used by the author-prefill hook
  // so we can seed template defaults without clobbering user edits across re-renders.
  const fieldKey = useCallback(
    (f: TemplateSigningField) => f.id ?? `${f.documentIndex ?? 1}:${f.fieldOrder}`,
    [],
  );

  // Union of AUTHOR-filled fields across all chosen templates (create mode).
  const authorFields = useMemo(
    () => signingFields.filter((f) => f.filledBy === 'AUTHOR'),
    [signingFields],
  );
  const hasAuthorFields = authorFields.length > 0;
  const hasPlaceholders = templatePlaceholders.length > 0;

  // Seed author-prefilled values from template defaults. StrictMode-safe; see hook.
  useSeedAuthorDefaults(authorFields, fieldKey, setPrefilledFieldValues);

  // Source for signer-slot mapping: every non-AUTHOR field carries a
  // template-time `recipientIndex` (1-based slot). Author-prefilled fields are
  // not owned by any signer and must be excluded.
  const mappingSourceFields = useMemo(
    () => signingFields.filter((f) => f.filledBy !== 'AUTHOR'),
    [signingFields],
  );

  // All declared signer slot numbers (1-based), sorted. Non-positive values
  // are legacy / bad template data (field.recipientIndex = 0 or null) — they
  // can't be mapped to a recipient, so we don't surface them in the picker or
  // block the user with a "Slot 0" validation toast.
  const allSignerSlots = useMemo(
    () =>
      [
        ...new Set(
          mappingSourceFields
            .map((f) => f.recipientIndex)
            .filter((n): n is number => typeof n === 'number' && n > 0),
        ),
      ].sort((a, b) => a - b),
    [mappingSourceFields],
  );

  // Field-count per slot — for the "(N fields)" hint inside each slot option.
  const slotFieldCount = useMemo(() => {
    const m: Record<number, number> = {};
    for (const f of mappingSourceFields) {
      m[f.recipientIndex] = (m[f.recipientIndex] ?? 0) + 1;
    }
    return m;
  }, [mappingSourceFields]);

  // { [slot]: 0-based recipientIndex } — rebuilt from recipient.signerSlots.
  // Consumed by buildSigningRequestPayload (field.recipientIndex substitution).
  const derivedSignerMappings = useMemo(() => {
    const out: Record<number, number> = {};
    recipients.forEach((r, rIdx) => {
      for (const slot of r.signerSlots) out[slot] = rIdx;
    });
    return out;
  }, [recipients]);

  // Slot → recipient-position translation + author-value injection.
  // Used by Step 5 (Place Fields) where `recipients` matches the wizard array.
  const decoratedFields = useMemo(
    () =>
      signingFields.map((f) => {
        if (f.filledBy === 'AUTHOR') {
          return { ...f, value: prefilledFieldValues[fieldKey(f)] ?? f.defaultValue ?? '' };
        }
        const pos0 = derivedSignerMappings[f.recipientIndex];
        return pos0 !== undefined ? { ...f, recipientIndex: pos0 + 1 } : f;
      }),
    [signingFields, prefilledFieldValues, derivedSignerMappings, fieldKey],
  );

  // Preview-modal data: fields keep raw slot numbers, legend is slot-keyed.
  // Slot N → previewRecipients[N - 1] so colors line up. When a slot is mapped
  // to a recipient, the legend shows "Signer N — Name"; otherwise just "Signer N".
  const previewFields = useMemo(
    () =>
      signingFields.map((f) =>
        f.filledBy === 'AUTHOR'
          ? { ...f, value: prefilledFieldValues[fieldKey(f)] ?? f.defaultValue ?? '' }
          : f,
      ),
    [signingFields, prefilledFieldValues, fieldKey],
  );

  const previewRecipients = useMemo(() => {
    if (allSignerSlots.length === 0) return [];
    const maxSlot = Math.max(...allSignerSlots);
    return Array.from({ length: maxSlot }, (_, i) => {
      const slot = i + 1;
      const recipientIdx = derivedSignerMappings[slot];
      const r = recipientIdx !== undefined ? recipients[recipientIdx] : undefined;
      const slotLabel = `${t('K_DS_SIGNER', 'Signer')} ${slot}`;
      const name = r?.name?.trim() ? `${slotLabel} — ${r.name}` : slotLabel;
      return {
        name,
        role: r?.role ?? 'SIGNER',
        signingOrder: r?.signingOrder,
      };
    });
  }, [allSignerSlots, derivedSignerMappings, recipients, t]);

  // Drop slots that are no longer declared by the templates (e.g. templates
  // swapped on Step 0 after the user mapped). Runs whenever allSignerSlots
  // changes; no-op if every owned slot is still valid.
  useEffect(() => {
    if (allSignerSlots.length === 0) return;
    const valid = new Set(allSignerSlots);
    setRecipients((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        const pruned = r.signerSlots.filter((s) => valid.has(s));
        if (pruned.length !== r.signerSlots.length) {
          changed = true;
          return { ...r, signerSlots: pruned };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [allSignerSlots]);

  const updatePrefilledFieldValue = useCallback((key: string, value: string) => {
    setPrefilledFieldValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ---- Placeholder input rendering (typed by fieldType) ----
  // Matches the Step 3 design: clean label + typed input. CHECKBOX renders as a
  // labelled checkbox; everything else is a typed <input> resolved via a lookup
  // table. Lookup + useMemo is cheaper than the chained ternary and reads at a
  // glance as "DATE → date input, placeholder 'Select date'".
  type PhFieldType = NonNullable<TemplatePlaceholder['fieldType']>;
  const INPUT_TYPE_BY_FIELD: Record<PhFieldType, 'date' | 'number' | 'email' | 'tel' | 'text'> = useMemo(
    () => ({
      DATE: 'date',
      NUMBER: 'text',
      CURRENCY: 'text',
      EMAIL: 'email',
      PHONE: 'tel',
      TEXT: 'text',
      CHECKBOX: 'text',
      SIGNATURE: 'text',
      DROPDOWN: 'text',
    }),
    [],
  );
  const INPUT_MODE_BY_FIELD: Partial<Record<PhFieldType, 'decimal' | 'numeric' | 'tel' | 'email'>> = useMemo(
    () => ({
      NUMBER: 'decimal',
      CURRENCY: 'decimal',
      PHONE: 'tel',
      EMAIL: 'email',
    }),
    [],
  );
  const PLACEHOLDER_TEXT_BY_FIELD: Record<PhFieldType, string> = useMemo(
    () => ({
      DATE: t('K_DS_PLACEHOLDER_SELECT_DATE', 'Select date'),
      NUMBER: t('K_DS_PLACEHOLDER_ENTER_NUMBER', 'Enter number'),
      CURRENCY: t('K_DS_PLACEHOLDER_ENTER_AMOUNT', 'Enter amount'),
      EMAIL: t('K_DS_PLACEHOLDER_ENTER_EMAIL', 'Enter email address'),
      PHONE: t('K_DS_PLACEHOLDER_ENTER_PHONE', 'Enter phone number'),
      TEXT: t('K_DS_PLACEHOLDER_ENTER_VALUE', 'Enter value...'),
      CHECKBOX: '',
      SIGNATURE: t('K_DS_PLACEHOLDER_ENTER_VALUE', 'Enter value...'),
      DROPDOWN: t('K_DS_PLACEHOLDER_ENTER_VALUE', 'Enter value...'),
    }),
    [t],
  );

  const renderPlaceholderInput = useCallback(
    (
      fieldType: TemplatePlaceholder['fieldType'] | undefined,
      placeholderKey: string,
      value: string,
      onChange: (v: string) => void,
    ) => {
      const ft: PhFieldType = fieldType ?? 'TEXT';
      if (ft === 'CHECKBOX') {
        return (
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={value === 'true'}
              onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
            />
            <span>{t('K_DS_PLACEHOLDER_VALUE_CHECKED', 'Yes')}</span>
          </label>
        );
      }
      if (ft === 'DATE') {
        return (
          <DatePicker
            value={value}
            onChange={onChange}
            placeholder={PLACEHOLDER_TEXT_BY_FIELD.DATE}
            fullWidth
          />
        );
      }
      return (
        <TextInput
          type={INPUT_TYPE_BY_FIELD[ft]}
          inputMode={INPUT_MODE_BY_FIELD[ft]}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={PLACEHOLDER_TEXT_BY_FIELD[ft]}
          data-placeholder-key={placeholderKey}
          fullWidth
        />
      );
    },
    [t, INPUT_TYPE_BY_FIELD, INPUT_MODE_BY_FIELD, PLACEHOLDER_TEXT_BY_FIELD],
  );

  // ---- Dynamic step skipping ----
  // Mirrors document-signing's skip rules so the user never lands on a step that
  // has nothing to do. Applied post-advance via the effect below so we don't
  // patch useMultiStepForm's linear API.
  // Retrigger-keep-signed flow: user hit "Edit & Resend" on a partially-signed
  // envelope and chose `continue` (keep existing signatures). Only envelope-
  // level metadata can change — skip every structural step. This is DIFFERENT
  // from `mode === 'continue'` alone, which is DRAFT resume (user paused mid-
  // wizard and wants to finish) — in that case every step stays visible.
  const isRetriggerKeepSignedMode = mode === 'edit' && retriggerMode === 'continue';
  const isStepVisible = useCallback(
    (i: number): boolean => {
      switch (i) {
        case 0:
          // Add Docs — hidden for retrigger-keep-signed (docs are immutable
          // there) AND for the REVIEW_COMPLETED parking state until the user
          // accepts the back-button warning.
          return !isRetriggerKeepSignedMode && !isReviewCompletedEntry;
        case 1:
          // Recipients & Signers — always visible except on REVIEW_COMPLETED
          // entry. Signer picker inside hides itself when there are no
          // declared slots (empty hint). In retrigger-keep-signed the picker
          // renders disabled (read-only).
          return !isReviewCompletedEntry;
        case 2: {
          // Fill Placeholders — show if either author fields or any placeholders exist.
          // Hidden in retrigger-keep-signed (values are locked to the already-sent state)
          // and in the REVIEW_COMPLETED parking state.
          if (isRetriggerKeepSignedMode) return false;
          if (isReviewCompletedEntry) return false;
          const hasResolvedValues = placeholderValues.length > 0;
          return hasAuthorFields || hasPlaceholders || hasResolvedValues;
        }
        case 3:
          // Review (preview) — skip in retrigger-keep-signed and on
          // REVIEW_COMPLETED entry (review already happened).
          return !isRetriggerKeepSignedMode && !isReviewCompletedEntry;
        case 4:
          // Place Fields — skip in retrigger-keep-signed (fields are immutable).
          // Always visible on REVIEW_COMPLETED entry — that's the landing step.
          return !isRetriggerKeepSignedMode;
        case 5:
        default:
          return true;
      }
    },
    [isRetriggerKeepSignedMode, isReviewCompletedEntry, hasAuthorFields, hasPlaceholders, placeholderValues.length],
  );

  // After currentStep changes, auto-advance/retreat past hidden steps based on
  // direction. Uses `setStepDirect` (no validation) — a hidden step is a UI
  // decision, not a user action, so running `onValidateStep` on it is wrong
  // (it would fire stale-state toasts during edit-mode hydration and cascade
  // validation across every skipped step).
  useEffect(() => {
    if (isStepVisible(currentStep)) return;
    let target = currentStep;
    if (direction === 1) {
      while (target < steps.length - 1 && !isStepVisible(target)) target++;
    } else {
      while (target > 0 && !isStepVisible(target)) target--;
    }
    if (target !== currentStep && isStepVisible(target)) {
      setStepDirect(target);
    }
  }, [currentStep, direction, isStepVisible, steps.length, setStepDirect]);

  // If the initial step lands on a hidden step (retrigger-keep-signed opens
  // with startStep default = 0, but step 0 is hidden in that mode), jump to
  // the first visible step on mount. Guarded by a ref so we only do this once
  // per wizard lifetime.
  const didClampInitialRef = useRef(false);
  useEffect(() => {
    if (didClampInitialRef.current) return;
    if (isStepVisible(currentStep)) {
      didClampInitialRef.current = true;
      return;
    }
    let target = currentStep;
    while (target < steps.length - 1 && !isStepVisible(target)) target++;
    if (isStepVisible(target) && target !== currentStep) {
      setStepDirect(target);
    }
    didClampInitialRef.current = true;
  }, [currentStep, isStepVisible, steps.length, setStepDirect]);

  // `startStep` is passed through to `useMultiStepForm` as `initialStep` — the
  // hook seeds currentStep directly. We do NOT replay goToNext for two reasons:
  // (1) validation would run against stale closures on mount while hydration
  // is still in flight; (2) Continue Editing explicitly means "all prior-step
  // data is already saved on the envelope — skip validation, trust the draft."

  // ---- File upload handler ----
  // PDF uploads → blob URL straight from the File (no server hop needed).
  // DOCX uploads → server-side `convertToPdf` so react-pdf can render a preview
  // in Step 5. Uses nextDocOrderRef so batches get contiguous documentOrder
  // values that match what goes into docPdfUrls as a key.
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) continue;
      const isPdf = file.type === 'application/pdf';
      const isDocx = file.name.endsWith('.docx');

      if (!isPdf && !isDocx) {
        addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_FILE_TYPE', 'Only PDF and DOCX files are supported.') });
        continue;
      }

      try {
        const fileRef = await dsFileService.upload(file);
        const docOrder = nextDocOrderRef.current;
        nextDocOrderRef.current++;

        setDocuments((prev) => [
          ...prev,
          {
            file,
            name: file.name,
            documentOrder: docOrder,
            sourceFileId: fileRef.id,
            pageCount: 1,
            sourceContentType: isPdf
              ? 'application/pdf'
              : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
        ]);

        if (isPdf) {
          const url = URL.createObjectURL(file);
          setDocPdfUrls((prev) => ({ ...prev, [docOrder]: url }));
        } else {
          // DOCX — ask backend to convert to PDF so we can show a preview.
          // Fire-and-forget; if it fails Step 5 will still render via OnlyOffice.
          dsFileService
            .convertToPdf(fileRef.id)
            .then((res) => {
              if (!res.data || res.data.size === 0) return;
              const url = URL.createObjectURL(res.data);
              setDocPdfUrls((prev) => ({ ...prev, [docOrder]: url }));
            })
            .catch(() => {
              /* DOCX preview unavailable — Step 5 falls back to its empty state */
            });
        }
      } catch {
        addNotification({ variant: 'critical', title: t('K_DS_NOTIF_UPLOAD_FAILED', 'Failed to upload file') });
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addNotification, t]);

  // (handleTemplateSelect removed — single-template path is superseded by
  // handleTemplatesPicked, which routes through TemplatePickerModal and
  // supports multi-template + full per-doc metadata.)

  const removeDocument = useCallback((index: number) => {
    setDocuments((prev) => {
      const removed = prev[index];
      if (!removed) return prev;
      const removedOrder = removed.documentOrder;

      // Revoke the blob for the removed doc, drop its entry, shift remaining
      // entries down so the map stays keyed by contiguous 1-based documentOrder.
      setDocPdfUrls((prevUrls) => {
        const toRevoke = prevUrls[removedOrder];
        if (toRevoke) URL.revokeObjectURL(toRevoke);
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(prevUrls)) {
          const key = Number(k);
          if (key === removedOrder) continue;
          next[key > removedOrder ? key - 1 : key] = v;
        }
        return next;
      });

      // Same reindex for the per-doc template metadata map.
      setTemplateDetailsByDocOrder((prevDetails) => {
        const next: Record<number, DocumentSnapshot> = {};
        for (const [k, v] of Object.entries(prevDetails)) {
          const key = Number(k);
          if (key === removedOrder) continue;
          next[key > removedOrder ? key - 1 : key] = v;
        }
        return next;
      });

      // Reindex signingFields: drop fields on the removed doc, shift docIndex
      // down for fields above it.
      setSigningFields((prevFields) =>
        prevFields
          .filter((f) => f.documentIndex !== removedOrder)
          .map((f) =>
            f.documentIndex && f.documentIndex > removedOrder
              ? { ...f, documentIndex: f.documentIndex - 1 }
              : f,
          ),
      );

      // If the active doc was the one removed, fall back to doc 1. If it was
      // above the removed doc, shift down to keep pointing at the same file.
      setActiveDocumentOrder((prevActive) => {
        if (prevActive === removedOrder) return 1;
        if (prevActive > removedOrder) return prevActive - 1;
        return prevActive;
      });

      // Re-normalize documents: drop removed, renumber 1..N.
      return prev.filter((_, i) => i !== index).map((d, i) => ({ ...d, documentOrder: i + 1 }));
    });

    // Keep nextDocOrderRef in sync — next new doc should land AFTER the new max.
    // The useEffect syncing it against `documents.length` handles this on
    // render but we also nudge it here so subsequent adds in the same tick
    // don't collide.
    nextDocOrderRef.current = Math.max(1, documents.length);

    if (selectedTemplate && documents.length <= 1) {
      setSelectedTemplate(null);
    }
  }, [selectedTemplate, documents.length]);

  // ---- Multi-template picker: fetch details + placeholders + signing fields per snapshot ----
  // All three calls go through `dsCrossOrgSnapshotService` because the picker
  // routes through cross-org snapshot search (tenant-scoped approved copies),
  // NOT `dsTemplateService` (workpay master templates). Same DTO shapes on both
  // services so the downstream state is unchanged.
  const handleTemplatesPicked = useCallback(
    async (picked: DocumentSnapshot[]) => {
      if (picked.length === 0) return;
      const appendedDocs: WizardDocument[] = [];
      const appendedFields: TemplateSigningField[] = [];
      const detailsUpdates: Record<number, DocumentSnapshot> = {};
      const placeholderUpdates: Array<
        TemplatePlaceholder & { documentOrder?: number; documentName?: string }
      > = [];
      const placeholderValueUpdates: Record<string, string> = {};

      for (let i = 0; i < picked.length; i++) {
        const snap = picked[i]!;
        const docOrder = nextDocOrderRef.current;
        nextDocOrderRef.current++;
        try {
          const [details, placeholders, fields] = await Promise.all([
            dsCrossOrgSnapshotService.getById(snap.id),
            dsCrossOrgSnapshotService
              .getDataPlaceholders(snap.id)
              .catch(() => [] as TemplatePlaceholder[]),
            dsCrossOrgSnapshotService
              .getSigningFields(snap.id)
              .catch(() => [] as TemplateSigningField[]),
          ]);

          detailsUpdates[docOrder] = details;
          appendedDocs.push({
            name: details.name,
            documentOrder: docOrder,
            sourceFileId: details.sourceFileId,
            pageCount: details.pageCount || 1,
            sourceContentType:
              details.templateFormat === 'PDF'
                ? 'application/pdf'
                : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            // Tenant-scoped snapshot id IS the document_template row that
            // contract-service validates against the target employee's
            // classification (country / legalEntity / employmentType).
            sourceTemplateId: details.id,
          });

          // Fetch the preview PDF per doc so Step 5's TemplateBuilder has a
          // blob URL to render. DOCX snapshots → server-side preview. PDF
          // snapshots → direct file download. Both fire-and-forget; the Step 5
          // loader guards the render until URLs land.
          if (details.sourceFileId) {
            if (details.templateFormat === 'DOCX') {
              dsCrossOrgSnapshotService
                .preview(details.id)
                .then((res) => {
                  if (!res.data || res.data.size === 0) return;
                  const url = URL.createObjectURL(res.data);
                  setDocPdfUrls((prev) => ({ ...prev, [docOrder]: url }));
                })
                .catch(() => { /* preview unavailable — Step 5 falls back to loader */ });
            } else {
              dsFileService
                .download(details.sourceFileId)
                .then((res) => {
                  if (!res.data || res.data.size === 0) return;
                  const url = URL.createObjectURL(res.data);
                  setDocPdfUrls((prev) => ({ ...prev, [docOrder]: url }));
                })
                .catch(() => { /* source unavailable — non-fatal */ });
            }
          }

          for (const f of fields) {
            const isAuthor = f.filledBy === 'AUTHOR';
            // Heal: if a SIGNER field arrives from the template without a
            // valid recipientIndex (legacy / bad template data), default to
            // slot 1 so it's at least mappable in the picker / Place Fields.
            const safeRecipientIdx = isAuthor
              ? (f.recipientIndex ?? 0)
              : f.recipientIndex && f.recipientIndex > 0
                ? f.recipientIndex
                : 1;
            appendedFields.push({
              ...f,
              recipientIndex: safeRecipientIdx,
              documentIndex: docOrder,
            });
          }

          for (const ph of placeholders) {
            placeholderUpdates.push({
              ...ph,
              documentOrder: docOrder,
              documentName: details.name,
            });
            if (ph.defaultValue && !(ph.placeholderKey in placeholderValueUpdates)) {
              placeholderValueUpdates[ph.placeholderKey] = ph.defaultValue;
            }
          }
        } catch {
          addNotification({
            variant: 'critical',
            title: t('K_DS_NOTIF_TEMPLATE_LOAD_FAILED', 'Failed to load template'),
            description: snap.name,
          });
        }
      }

      if (appendedDocs.length === 0) return;

      // `selectedTemplate` is typed as DocumentTemplate for legacy reasons; the
      // snapshot shape is structurally identical for the fields the wizard reads
      // (id, name). Cast here until we widen the selectedTemplate type.
      const [primary] = picked;
      if (primary && !selectedTemplate) {
        setSelectedTemplate(primary as unknown as DocumentTemplate);
        if (!subject.trim()) setSubject(primary.name);
      }
      setDocuments((prev) => [...prev, ...appendedDocs]);
      setTemplateDetailsByDocOrder((prev) => ({ ...prev, ...detailsUpdates }));
      setSigningFields((prev) => [...prev, ...appendedFields]);
      setTemplatePlaceholders((prev) => {
        // Dedupe against both prior state AND the current batch — two picked
        // templates often share the same placeholder key (e.g. "Agreement Date",
        // "Notice Period for Resignation"). Without the intra-batch guard,
        // React's list render hits duplicate `key={placeholderKey}` warnings.
        const seen = new Set(prev.map((p) => p.placeholderKey));
        const deduped: typeof placeholderUpdates = [];
        for (const p of placeholderUpdates) {
          if (seen.has(p.placeholderKey)) continue;
          seen.add(p.placeholderKey);
          deduped.push(p);
        }
        return [...prev, ...deduped];
      });
      setPlaceholderValueMap((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(placeholderValueUpdates)) {
          if (!(k in next)) next[k] = v;
        }
        return next;
      });
    },
    [documents.length, selectedTemplate, subject, addNotification, t],
  );

  // (fieldKey / authorFields / has* flags moved above step-skipping so the
  // isStepVisible callback can depend on them — see top of component body.)

  // ---- Recipient management ----
  const addRecipient = useCallback(() => {
    setRecipients((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: '',
        email: '',
        role: 'SIGNER',
        signingMethod: 'ELECTRONIC',
        signingOrder: prev.length + 1,
        recipientType: 'EXTERNAL',
        signerSlots: [],
      },
    ]);
  }, []);

  // Role flip to CC clears owned slots — CC recipients cannot sign.
  // Validator will surface any orphaned slots on step advance.
  const updateRecipient = useCallback(
    (index: number, field: keyof WizardRecipient, value: string) => {
      setRecipients((prev) =>
        prev.map((r, i) => {
          if (i !== index) return r;
          if (field === 'role' && value === 'CC' && r.signerSlots.length > 0) {
            return { ...r, role: value as RecipientRole, signerSlots: [] };
          }
          return { ...r, [field]: value };
        }),
      );
    },
    [],
  );

  const setRecipientSignerSlots = useCallback((index: number, slots: number[]) => {
    setRecipients((prev) =>
      prev.map((r, i) => (i === index ? { ...r, signerSlots: [...slots].sort((a, b) => a - b) } : r)),
    );
  }, []);

  // Removed recipient's slots are dropped (no auto-reassign). Validator flags
  // them as unmapped on next advance — forces the author to decide.
  const removeRecipient = useCallback((index: number) => {
    setRecipients((prev) =>
      prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, signingOrder: i + 1 })),
    );
  }, []);

  // ---- Build signing request payload ----
  const buildSigningRequestPayload = useCallback((): CreateEnvelopeRequest => {
    const validRecipients = recipients.filter((r) => r.name.trim() && r.email.trim());
    const envelopeDocuments: EnvelopeDocument[] = documents.map((d) => ({
      name: d.name,
      documentOrder: d.documentOrder,
      sourceFileId: d.sourceFileId,
      pageCount: d.pageCount,
      sourceContentType: d.sourceContentType,
      ...(d.sourceTemplateId ? { sourceTemplateId: d.sourceTemplateId } : {}),
    }));

    const envelopeRecipients: EnvelopeRecipient[] = validRecipients.map((r) => {
      const recipientType: RecipientType = r.recipientType ?? 'EXTERNAL';
      return {
        // Send backend recipient id when this row was hydrated from an
        // existing envelope. editAndRetrigger?mode=continue requires it to
        // match the persisted recipient row — without it the resend email
        // never fires for the existing signer. New rows omit `id` so the
        // backend mints a fresh recipient_id.
        ...(r.persistedId ? { id: r.persistedId } : {}),
        name: r.name,
        email: r.email,
        role: r.role,
        status: 'PENDING' as const,
        signingMethod: r.signingMethod,
        signingOrder: r.signingOrder,
        recipientType,
        // `userId` is required only for INTERNAL; backend ignores it for EXTERNAL.
        ...(recipientType === 'INTERNAL' && r.userId ? { userId: r.userId } : {}),
      };
    });

    // Defer persisting fields until every template signer slot has been mapped
    // to a real recipient. The backend has no `recipient_index` column on
    // `envelope_field` — it converts `recipientIndex` to a `recipientId` UUID at
    // write time. When a slot is unmapped (or maps to a position with no
    // recipient yet, e.g. Step 0 → 1 autosave running before recipient-prefill
    // populates the second recipient), the backend stores `recipient_id = NULL`
    // and the original slot identity is lost permanently on read. Shipping
    // `fields: []` here means: no field rows on the backend until mapping is
    // complete; on the Step 1 → 2 autosave (which can't fire without all slots
    // mapped — see the validator at "K_DS_VALIDATION_MAP_SIGNERS"), we ship the
    // fully-mapped set. Hydration re-sources field positions from picked
    // templates so Continue Editing still shows the slot dropdown correctly.
    const allSlotsMapped =
      allSignerSlots.length === 0 ||
      allSignerSlots.every((slot) => derivedSignerMappings[slot] !== undefined);

    const envelopeFields: EnvelopeField[] = !allSlotsMapped
      ? []
      : signingFields.map((f, idx) => {
          const key = fieldKey(f);
          const isAuthor = f.filledBy === 'AUTHOR';
          const mapped = !isAuthor ? derivedSignerMappings[f.recipientIndex] : undefined;
          return {
            fieldType: f.fieldType,
            label: f.label,
            pageNumber: f.pageNumber,
            xPosition: f.xPosition,
            yPosition: f.yPosition,
            width: f.width,
            height: f.height,
            isRequired: f.isRequired ?? false,
            fontSize: f.fontSize,
            defaultValue: f.defaultValue ?? null,
            dropdownOptions: f.dropdownOptions ?? null,
            fieldOrder: f.fieldOrder ?? idx,
            isReadOnly: f.isReadOnly ?? false,
            recipientIndex: mapped !== undefined ? mapped + 1 : f.recipientIndex,
            documentIndex: f.documentIndex ?? 0,
            filledBy: f.filledBy,
            // Carry the author-typed value so the server stamps it onto the final PDF.
            ...(isAuthor ? { value: prefilledFieldValues[key] ?? f.defaultValue ?? '' } : {}),
          };
        });

    return {
      templateId: selectedTemplate?.id,
      subject,
      message: message || undefined,
      // Envelope-level `signingMethod` is legacy on the backend DTO — per-recipient
      // `signingMethod` on each EnvelopeRecipient is the authoritative value.
      signingMethod: 'ELECTRONIC',
      documents: envelopeDocuments,
      recipients: envelopeRecipients,
      fields: envelopeFields,
      // Expiry — stamped to end-of-day UTC. Undefined if the user never picked
      // a date (backend treats as "no expiry").
      ...(expiresAt
        ? { expiresAt: new Date(`${expiresAt}T23:59:59Z`).toISOString() }
        : {}),
      // Target-object binding — required by contract-service when generating
      // a contract against an employee. The backend validates that each
      // picked template/snapshot's (countryId, legalEntityId, employmentTypeId)
      // tuple matches this employee's classification; without this object the
      // request is rejected with 400 `ENVELOPE_TARGET_REQUIRED`. When the
      // wizard is opened without an employee context (e.g. legacy direct entry
      // to `/w/signing-requests/new`), we omit `target` entirely.
      //
      // Shape is nested per contract-service DTO: `target: { targetType, targetId }`.
      // NOT flat (a flat `targetType`/`targetId` at the root is silently ignored
      // by Jackson's `@JsonCreator` on the backend and still throws the above
      // error). See backend/contract-service/docs/api-endpoints.md.
      ...(employeeId
        ? { target: { targetType: 'EMPLOYEE' as const, targetId: employeeId } }
        : {}),
      // Drop blanks + intra-section dupes — backend `@Email` rejects empty
      // strings and we don't want notification-service to fan out 2× to one
      // CC/BCC address.
      cc: [...new Set(ccEmails.map((e) => e.trim()).filter(Boolean))],
      bcc: [...new Set(bccEmails.map((e) => e.trim()).filter(Boolean))],
    };
  }, [
    recipients,
    documents,
    signingFields,
    derivedSignerMappings,
    allSignerSlots,
    selectedTemplate,
    subject,
    message,
    fieldKey,
    prefilledFieldValues,
    employeeId,
    expiresAt,
    ccEmails,
    bccEmails,
  ]);

  // ---- Per-step autosave ("Save Draft") ----
  const skipAutoSave = mode === 'edit' && retriggerMode === 'continue';

  const persistDraft = useCallback(
    async (): Promise<Envelope | null> => {
      if (skipAutoSave) return null;
      if (!subject.trim() || documents.length === 0) return null;

      const payload = buildSigningRequestPayload();
      const currentDocsSig = buildDocsSig(documents);
      const currentRecipSig = buildRecipientsSig(recipients, ccEmails, bccEmails);
      const childrenChanged =
        lastSavedDocsSigRef.current !== currentDocsSig ||
        lastSavedRecipientsSigRef.current !== currentRecipSig;

      let saved: Envelope;
      let ranResolver = false;
      if (!createdRequestId) {
        saved = await dsEnvelopeService.create(payload);
        ranResolver = true;
        setCreatedRequestId(saved.id);
        queryClient.setQueryData(['dsEnvelope', saved.id], saved);
      } else if (mode === 'edit' || childrenChanged) {
        saved = await dsEnvelopeService.editAndRetrigger(
          createdRequestId,
          payload,
          'reset_all',
          false,
        );
        ranResolver = true;
      } else {
        saved = await dsEnvelopeService.update(createdRequestId, payload);
      }

      lastSavedDocsSigRef.current = currentDocsSig;
      lastSavedRecipientsSigRef.current = currentRecipSig;

      const savedDocs = saved.documents ?? [];
      setDocuments((prev) =>
        prev.map((d) => {
          const match = savedDocs.find((cd) => cd.documentOrder === d.documentOrder);
          return match?.id ? { ...d, id: match.id } : d;
        }),
      );

      if (ranResolver) {
        try {
          const fetched = await dsEnvelopeService.getPlaceholderValues(saved.id);
          if (fetched.length > 0) {
            setPlaceholderValues(fetched);
            const fieldTypeByKey = new Map(
              templatePlaceholdersRef.current.map((p) => [p.placeholderKey, p.fieldType]),
            );
            setPlaceholderValueMap((prev) => {
              const next = { ...prev };
              for (const v of fetched) {
                if (next[v.placeholderKey] !== undefined) continue;
                if (!v.resolvedValue) continue;
                next[v.placeholderKey] =
                  fieldTypeByKey.get(v.placeholderKey) === 'DATE'
                    ? backendDateToIso(v.resolvedValue)
                    : v.resolvedValue;
              }
              lastSavedPlaceholderSigRef.current = buildPlaceholderValuesSig(next);
              return next;
            });
          }
        } catch {
          /* non-fatal */
        }
      }

      return saved;
    },
    [
      skipAutoSave,
      subject,
      documents,
      recipients,
      ccEmails,
      bccEmails,
      mode,
      createdRequestId,
      buildSigningRequestPayload,
      queryClient,
    ],
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      const saved = await persistDraft();
      if (!saved) {
        throw new Error('Nothing to save yet');
      }
      return saved;
    },
    onSuccess: () => {
      addNotification({
        variant: 'success',
        title: t('K_DS_NOTIF_SIGNING_REQUEST_SAVED', 'Signing request saved as draft'),
      });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  // ---- Submit: final Send ----
  const sendMutation = useMutation({
    mutationFn: async () => {
      const payload = buildSigningRequestPayload();

      if (mode === 'edit') {
        if (!createdRequestId) {
          throw new Error('editAndRetrigger requires an existing envelope id');
        }
        await dsEnvelopeService.editAndRetrigger(
          createdRequestId,
          payload,
          retriggerMode,
          true,
        );
        return createdRequestId;
      }

      let reqId = createdRequestId;
      if (!reqId) {
        const created = await dsEnvelopeService.create(payload);
        reqId = created.id;
        setCreatedRequestId(reqId);
      } else {
        await dsEnvelopeService.update(reqId, payload);
      }
      await dsEnvelopeService.send(reqId);
      return reqId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsEnvelopes'] });
      if (createdRequestId) {
        void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', createdRequestId] });
      }
      addNotification({
        variant: 'success',
        title: t('K_DS_NOTIF_SIGNING_REQUEST_SENT', 'Signing request sent successfully'),
      });
      navigate({ to: routeTo });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleSubmit = useCallback(async () => {
    await sendMutation.mutateAsync();
  }, [sendMutation]);

  // Demote a REVIEW_COMPLETED envelope back to DRAFT so the wizard can reopen
  // earlier steps. There is no dedicated endpoint — we reuse
  // `editAndRetrigger` with `mode=reset_all` and `send=false`. The backend's
  // `requireDraft` accepts REVIEW_COMPLETED for this call, and the
  // child-rewrite path explicitly resets the envelope to DRAFT (per the
  // service-layer comment in commit 59f974af). `send=false` keeps it as a
  // draft instead of immediately re-sending to recipients.
  const revertMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveRequestId) {
        throw new Error('revert (editAndRetrigger) requires an existing envelope id');
      }
      const payload = buildSigningRequestPayload();
      return dsEnvelopeService.editAndRetrigger(
        effectiveRequestId,
        payload,
        'reset_all',
        false,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsEnvelopes'] });
      if (effectiveRequestId) {
        void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', effectiveRequestId] });
      }
      setShowRevertConfirm(false);
      setHasUserSteppedBack(true);
      // Drop one step. Step 3 (Review) is the natural previous step now that
      // 0–3 are visible; the auto-advance effect won't move us because step 3
      // is visible. Skipping straight to goToPrev keeps direction = -1.
      goToPrev();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleClose = useCallback(() => {
    navigate({ to: routeTo });
  }, [navigate, routeTo]);

  const handleNext = useCallback(async () => {
    if (isAdvancing) return;
    setIsAdvancing(true);
    try {
      if (isLastStep) {
        await handleSubmit();
      } else {
        await goToNext();
      }
    } finally {
      setIsAdvancing(false);
    }
  }, [isAdvancing, isLastStep, goToNext, handleSubmit]);

  // Wrap the stepper Previous button so REVIEW_COMPLETED entries cannot walk
  // back into earlier steps without first reverting the envelope to DRAFT.
  // Once the user accepts the warning + the revert mutation succeeds,
  // `hasUserSteppedBack` flips and `isReviewCompletedEntry` returns false on
  // every subsequent click — back navigation is unfettered after that.
  const handleStepperBack = useCallback(() => {
    if (isReviewCompletedEntry && currentStep === 4) {
      setShowRevertConfirm(true);
      return;
    }
    goToPrev();
  }, [isReviewCompletedEntry, currentStep, goToPrev]);

  // Translate fields coming back from TemplateBuilder.
  // TemplateBuilder/FieldPropertiesPanel/PdfViewer all treat `recipientIndex`
  // as the 1-based recipient position, but the wizard stores it as the slot
  // number internally. Convert position → slot here, allocating a fresh slot
  // for any recipient that doesn't yet own one (and adding it to that
  // recipient's signerSlots).
  const handlePlaceFieldsChange = useCallback(
    (newFields: TemplateSigningField[]) => {
      const slotsToAllocByRecipient: Record<number, number> = {};
      let nextSlot = allSignerSlots.length > 0 ? Math.max(...allSignerSlots) : 0;

      const translated = newFields.map((f) => {
        if (f.filledBy === 'AUTHOR') return f;
        const pos1 = f.recipientIndex;
        if (!pos1 || pos1 < 1) return f;
        const rIdx = pos1 - 1;
        const r = recipients[rIdx];
        if (!r) return f;
        let slot = r.signerSlots[0];
        if (slot === undefined) {
          slot = slotsToAllocByRecipient[rIdx];
          if (slot === undefined) {
            nextSlot += 1;
            slot = nextSlot;
            slotsToAllocByRecipient[rIdx] = slot;
          }
        }
        return { ...f, recipientIndex: slot };
      });

      setSigningFields(translated);

      const allocEntries = Object.entries(slotsToAllocByRecipient);
      if (allocEntries.length > 0) {
        setRecipients((prev) =>
          prev.map((r, idx) => {
            const newSlot = slotsToAllocByRecipient[idx];
            if (newSlot === undefined) return r;
            return {
              ...r,
              signerSlots: [...r.signerSlots, newSlot].sort((a, b) => a - b),
            };
          }),
        );
      }
    },
    [recipients, allSignerSlots],
  );

  // ---- Resolve placeholders when entering the Fill Placeholders step ----
  useEffect(() => {
    if (currentStep !== 2 || !createdRequestId) return;
    dsEnvelopeService.resolvePlaceholders(createdRequestId)
      .then((values) => setPlaceholderValues(values))
      .catch(() => {});
  }, [currentStep, createdRequestId]);

  // ---- Regenerate PDFs when user revisits Place Fields with updated placeholders ----
  // Per-docOrder ref: stores `${snapshotId}:${sortedMergedValues}` — skips the
  // regen when the effect re-fires with identical inputs. Catches the
  // edit-hydration cascade (one PDF generated there; regen effect would
  // otherwise re-fire with the same state and burn a second call).
  const prevGeneratedUrlsRef = useRef<Record<number, string>>({});
  const lastGenSigRef = useRef<Record<number, string>>({});
  useEffect(() => {
    if (currentStep !== 4) return;
    const entries = Object.entries(templateDetailsByDocOrder)
      .map(([k, v]) => ({ order: Number(k), details: v }))
      .filter(({ details }) => details.templateFormat === 'DOCX');
    if (entries.length === 0) return;

    const merged: Record<string, string> = {};
    for (const [k, v] of Object.entries(placeholderValueMap)) {
      if (v.trim()) merged[k] = v;
    }
    for (const pv of placeholderValues) {
      if (pv.resolvedValue && pv.resolvedValue.trim()) {
        merged[pv.placeholderKey] = pv.resolvedValue;
      }
    }
    const sig = Object.keys(merged).sort().map((k) => `${k}=${merged[k]}`).join('|');

    const toGenerate = entries.filter(
      ({ order, details }) => lastGenSigRef.current[order] !== `${details.id}:${sig}`,
    );
    if (toGenerate.length === 0) return;

    let cancelled = false;
    setPdfRegenerating(true);

    Promise.all(
      toGenerate.map(async ({ order, details }) => {
        lastGenSigRef.current[order] = `${details.id}:${sig}`;
        try {
          const res = await dsCrossOrgSnapshotService.generatePdf(details.id, merged);
          if (cancelled || !res.data || res.data.size === 0) return;
          const url = URL.createObjectURL(res.data);
          setDocPdfUrls((prev) => {
            const prior = prevGeneratedUrlsRef.current[order];
            if (prior && prior !== url) URL.revokeObjectURL(prior);
            prevGeneratedUrlsRef.current[order] = url;
            return { ...prev, [order]: url };
          });
        } catch {
          /* per-template failure is non-fatal */
        }
      }),
    ).finally(() => {
      if (!cancelled) setPdfRegenerating(false);
    });

    return () => {
      cancelled = true;
    };
  }, [currentStep, templateDetailsByDocOrder, placeholderValueMap, placeholderValues]);

  // Step 4 is unsafe to leave (or render the builder for) until every doc's
  // preview blob has landed AND no DOCX regeneration is in flight. Without this
  // gate, Next would persist whatever stale `signingFields` are in state, and
  // the active doc can mount TemplateBuilder with `pdfUrl=undefined`.
  const placeFieldsPreviewReady =
    !pdfRegenerating &&
    documents.length > 0 &&
    documents.every((d) => !!docPdfUrls[d.documentOrder]);

  // ---- Render step content ----
  const renderStepContent = () => {
    switch (currentStep) {
      // Step 0: Add Documents
      case 0:
        return (
          <div className={styles.stepContent}>
            <div className={styles.formSection}>
              <label className={styles.label}>{t('K_DS_COLUMN_SUBJECT', 'Subject')}</label>
              <input
                type="text"
                className={styles.input}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('K_DS_PLACEHOLDER_SUBJECT', 'e.g., Employment Agreement - John Doe')}
              />
            </div>

            <div className={styles.formSection}>
              <label className={styles.label}>{t('K_DS_LABEL_MESSAGE', 'Message (optional)')}</label>
              <textarea
                className={styles.textarea}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('K_DS_PLACEHOLDER_MESSAGE', 'Optional message for recipients...')}
              />
            </div>

            <div className={styles.formSection}>
              <label className={styles.label}>{t('K_DS_LABEL_DOCUMENTS', 'Documents')}</label>
              {/* Single entry point for templates — the dashed drop-zone below
                  handles file upload, so no redundant "Upload File" button. */}
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="filled"
                  size="sm"
                  onClick={() => setShowTemplatePicker(true)}
                  type="button"
                >
                  {t('K_DS_STEP_DOCS_BTN_TEMPLATES', 'Pick Templates')}
                </Button>
              </div>
              <div className={styles.orDivider} aria-hidden="true">
                <span>{t('K_DS_OR', 'or')}</span>
              </div>
              <div
                className={styles.uploadArea}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  multiple
                  onChange={handleFileUpload}
                  className={styles.fileInput}
                />
                <div className={styles.uploadPrompt}>
                  <p>{t('K_DS_CLICK_TO_UPLOAD', 'Click to upload PDF or DOCX files')}</p>
                  <p className={styles.uploadHint}>{t('K_DS_OR_DRAG_DROP', 'or drag and drop here')}</p>
                </div>
              </div>

              {documents.length > 0 && (
                <div className={styles.documentList}>
                  {documents.map((doc, idx) => (
                    <div key={idx} className={styles.documentItem}>
                      <span className={styles.documentName}>{doc.name}</span>
                      <button
                        className={styles.removeBtn}
                        onClick={() => removeDocument(idx)}
                        type="button"
                      >
                        {t('K_DS_REMOVE', 'Remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      // Step 1: Recipients & Signers — split into two sections (2026-04-29).
      case 1: {
        const ownedByOthersByIndex = recipients.map((_, rIdx) => {
          const s = new Set<number>();
          recipients.forEach((other, oIdx) => {
            if (oIdx === rIdx) return;
            for (const slot of other.signerSlots) s.add(slot);
          });
          return s;
        });
        return (
          <div className={styles.stepContent}>
            {/* ── Section A: Signers ─────────────────────────────────── */}
            <section className={styles.section}>
              <header className={styles.sectionHeader}>
                <div className={styles.sectionHeaderText}>
                  <h3 className={styles.sectionTitle}>
                    {t('K_DS_SECTION_SIGNERS_TITLE', 'Signers')}
                  </h3>
                  <p className={styles.sectionHint}>
                    {t(
                      'K_DS_SECTION_SIGNERS_HINT',
                      'Each signer receives a personalised signing link. Order controls the sequence.',
                    )}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreviewModal(true)}
                  disabled={
                    signingFields.length === 0 || Object.keys(docPdfUrls).length === 0
                  }
                >
                  {t('K_DS_BUTTON_PREVIEW_DOCUMENTS', 'Preview Documents')}
                </Button>
              </header>

              <div className={styles.signersList}>
                {recipients.map((r, idx) => {
                  const ownedByOthers = ownedByOthersByIndex[idx] ?? new Set<number>();
                  const availableOptions: MultiSelectOption[] = allSignerSlots
                    .filter((slot) => !ownedByOthers.has(slot))
                    .map((slot) => ({
                      value: String(slot),
                      label: t('K_DS_SIGNER_SLOT', 'Signer {index}').replace(
                        '{index}',
                        String(slot),
                      ),
                      description: t(
                        'K_DS_SIGNER_SLOT_FIELD_COUNT',
                        '{count} field(s)',
                      ).replace('{count}', String(slotFieldCount[slot] ?? 0)),
                    }));
                  const selectedSlotValues = r.signerSlots.map(String);
                  const pickerDisabled =
                    isRetriggerKeepSignedMode || r.role === 'CC' || mode === 'edit';
                  return (
                    <div key={r.id} className={styles.recipientRow}>
                      <div className={styles.recipientFields}>
                        <div className={styles.formField}>
                          <label className={styles.fieldLabel}>
                            {t('K_DS_LABEL_NAME', 'Name')}
                          </label>
                          <input
                            type="text"
                            className={styles.input}
                            value={r.name}
                            onChange={(e) => updateRecipient(idx, 'name', e.target.value)}
                            placeholder={t(
                              'K_DS_PLACEHOLDER_RECIPIENT_NAME',
                              'Recipient name',
                            )}
                          />
                        </div>
                        <div className={styles.formField}>
                          <label className={styles.fieldLabel}>
                            {t('K_DS_LABEL_EMAIL', 'Email')}
                          </label>
                          <input
                            type="email"
                            className={styles.input}
                            value={r.email}
                            onChange={(e) => updateRecipient(idx, 'email', e.target.value)}
                            placeholder={t(
                              'K_DS_PLACEHOLDER_RECIPIENT_EMAIL',
                              'email@example.com',
                            )}
                          />
                        </div>
                        <div className={styles.formField}>
                          <label className={styles.fieldLabel}>
                            {t('K_DS_SIGNING_ORDER', 'Order')}
                          </label>
                          <input
                            type="number"
                            className={styles.input}
                            value={r.signingOrder}
                            min={1}
                            onChange={(e) =>
                              updateRecipient(idx, 'signingOrder', e.target.value)
                            }
                          />
                        </div>
                        {allSignerSlots.length > 0 && (
                          <MultiSelect
                            label={t('K_DS_LABEL_SIGNERS', 'Signers')}
                            options={availableOptions}
                            value={selectedSlotValues}
                            onChange={(vals) =>
                              setRecipientSignerSlots(
                                idx,
                                vals
                                  .map((v) => Number(v))
                                  .filter((n) => Number.isFinite(n)),
                              )
                            }
                            placeholder={t('K_DS_PLACEHOLDER_SIGNERS', 'Select signers...')}
                            disabled={pickerDisabled}
                            fullWidth
                          />
                        )}
                      </div>
                      {recipients.length > 1 && (
                        <button
                          className={styles.removeBtn}
                          onClick={() => removeRecipient(idx)}
                          type="button"
                        >
                          {t('K_DS_REMOVE', 'Remove')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {allSignerSlots.length === 0 && signingFields.length > 0 && (
                <p className={styles.stepDescription}>
                  {t(
                    'K_DS_HINT_SIGNER_SLOTS_EMPTY',
                    'No signers required on the selected templates.',
                  )}
                </p>
              )}

              <Button
                variant="outline"
                onClick={addRecipient}
                disabled={isRetriggerKeepSignedMode}
              >
                {t('K_DS_BUTTON_ADD_SIGNER', 'Add signer')}
              </Button>
            </section>

            {/* ── Section B: Other Recipients (CC / BCC) ─────────────── */}
            <section className={styles.section}>
              <header className={styles.sectionHeader}>
                <div className={styles.sectionHeaderText}>
                  <h3 className={styles.sectionTitle}>
                    {t('K_DS_SECTION_OTHER_RECIPIENTS_TITLE', 'Other Recipients')}
                  </h3>
                  <p className={styles.sectionHint}>
                    {t(
                      'K_DS_SECTION_OTHER_RECIPIENTS_HINT',
                      'Add CC and BCC email addresses. They receive a copy of every email notification but cannot sign or comment.',
                    )}
                  </p>
                </div>
              </header>

              <EmailTagsInput
                label={t('K_DS_LABEL_CC', 'CC')}
                value={ccEmails}
                onChange={setCcEmails}
                placeholder={t('K_DS_PLACEHOLDER_CC_EMAIL', 'cc@example.com')}
              />
              <EmailTagsInput
                label={t('K_DS_LABEL_BCC', 'BCC')}
                value={bccEmails}
                onChange={setBccEmails}
                placeholder={t('K_DS_PLACEHOLDER_BCC_EMAIL', 'bcc@example.com')}
              />
            </section>
          </div>
        );
      }

      // Step 2: Fill Placeholders (DOCX placeholders + author-prefilled fields)
      case 2: {
        const nothingToFill =
          !hasAuthorFields && templatePlaceholders.length === 0;
        return (
          <div className={styles.stepContent}>
            {hasAuthorFields && (
              <div className={styles.reviewSection}>
                <h3 className={styles.reviewTitle}>
                  {t('K_DS_STEP_FILL_PLACEHOLDERS_AUTHOR_TITLE', 'Prefilled by you')}
                </h3>
                <p className={styles.placeholderSource}>
                  {t(
                    'K_DS_STEP_FILL_PLACEHOLDERS_AUTHOR_HINT',
                    'These fields will be stamped onto the PDF before sending. Reviewers and signers will see the final values.',
                  )}
                </p>
                <div className={styles.placeholderList}>
                  {authorFields.map((f) => {
                    const key = fieldKey(f);
                    return (
                      <div key={key} className={styles.formSection}>
                        <PrefilledAuthorFieldInput
                          field={f}
                          value={prefilledFieldValues[key]}
                          onChange={(v) => updatePrefilledFieldValue(key, v)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {templatePlaceholders.length > 0 && (
              <div className={styles.reviewSection}>
                <h3 className={styles.reviewTitle}>
                  {t('K_DS_STEP_FILL_PLACEHOLDERS_DATA_TITLE', 'Document placeholders')}
                </h3>
                <div className={styles.placeholderList}>
                  {templatePlaceholders.map((ph) => (
                    <div key={ph.placeholderKey} className={styles.formSection}>
                      <label className={styles.label}>
                        {ph.displayLabel || ph.placeholderKey}
                        {ph.dataSourceType && ph.dataSourceType !== 'CUSTOM' && (
                          <span className={styles.placeholderSource}> ({ph.dataSourceType})</span>
                        )}
                      </label>
                      {renderPlaceholderInput(
                        ph.fieldType,
                        ph.placeholderKey,
                        placeholderValueMap[ph.placeholderKey] ?? '',
                        (v) =>
                          setPlaceholderValueMap((prev) => ({
                            ...prev,
                            [ph.placeholderKey]: v,
                          })),
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {nothingToFill && (
              <div className={styles.emptyStep}>
                <p>
                  {t('K_DS_NO_PLACEHOLDERS', 'No data placeholders found in the documents.')}
                </p>
              </div>
            )}
          </div>
        );
      }

      // Step 3: Optional "Send for Review" — matches document-signing.
      // Saves the draft signing request (needed for a reviewId), persists placeholder
      // values, then opens SendForReviewModal. If the user skips, Next advances
      // directly to Place Fields.
      case 3:
        return (
          <div className={styles.stepContent}>
            <p className={styles.stepDescription}>
              {t(
                'K_DS_STEP_REVIEW_DESC',
                'Optionally send your document for review before placing signing fields. Reviewers can add comments and suggest changes directly in the document.',
              )}
            </p>
            <div className={styles.reviewSection}>
              <Button
                variant="outline"
                onClick={async () => {
                  // Save draft first so the review can attach to a signing request id.
                  let reqId = createdRequestId;
                  if (!reqId) {
                    const saved = await createMutation.mutateAsync();
                    reqId = saved.id;
                  }
                  if (reqId && Object.keys(placeholderValueMap).length > 0) {
                    const values: EnvelopeDataValue[] = Object.entries(placeholderValueMap).map(
                      ([key, val]) => {
                        const ph = templatePlaceholders.find((p) => p.placeholderKey === key);
                        const resolvedValue =
                          ph?.fieldType === 'DATE' ? isoDateToBackendFormat(val) : val;
                        return { placeholderKey: key, resolvedValue } as EnvelopeDataValue;
                      },
                    );
                    try {
                      await dsEnvelopeService.updatePlaceholderValues(reqId, values);
                    } catch {
                      /* non-fatal — user can still proceed, values retry at send */
                    }
                  }
                  setShowReviewModal(true);
                }}
                loading={createMutation.isPending}
              >
                {t('K_DS_SEND_FOR_REVIEW', 'Send for Review')}
              </Button>
              <p className={styles.reviewHint}>
                {t(
                  'K_DS_STEP_REVIEW_SKIP_HINT',
                  'Or click Next to skip review and go straight to placing signing fields.',
                )}
              </p>
            </div>
          </div>
        );

      // Step 4: Place Fields
      case 4: {
        // Two loading states: active regeneration after placeholder fill, OR
        // still waiting on the initial preview fetch (handleTemplatesPicked fires
        // previews fire-and-forget, so per-doc URLs land asynchronously).
        if (!placeFieldsPreviewReady) {
          return (
            <div className={styles.stepContent}>
              <div className={styles.emptyStep}>
                <p>
                  {t(
                    'K_DS_STEP_PLACE_FIELDS_REGENERATING',
                    'Generating document preview...',
                  )}
                </p>
              </div>
            </div>
          );
        }
        const activeDoc = documents.find((d) => d.documentOrder === activeDocumentOrder);
        return (
          <div className={styles.builderWrap}>
            <TemplateBuilder
              templateFormat="PDF"
              pdfUrl={docPdfUrls[activeDocumentOrder]}
              pageCount={activeDoc?.pageCount || 1}
              documents={documents.map((d) => ({
                name: d.name,
                documentOrder: d.documentOrder,
                sourceFileId: d.sourceFileId,
                pageCount: d.pageCount,
              }))}
              docPdfUrls={docPdfUrls}
              activeDocumentOrder={activeDocumentOrder}
              onActiveDocumentChange={setActiveDocumentOrder}
              initialFields={decoratedFields}
              onFieldsChange={handlePlaceFieldsChange}
              onPageCountChange={(count) => {
                // Keep the doc's pageCount in sync with what react-pdf actually
                // rendered — useful when the source template pageCount was stale
                // or when PDF regeneration produced a different page count.
                setDocuments((prev) =>
                  prev.map((d) =>
                    d.documentOrder === activeDocumentOrder ? { ...d, pageCount: count } : d,
                  ),
                );
              }}
              recipientCount={recipients.filter((r) => r.role === 'SIGNER' || r.role === 'IN_PERSON_SIGNER').length}
              recipients={recipients.map((r) => ({
                name: r.name,
                role: r.role,
                signingOrder: r.signingOrder,
              }))}
              disableAuthorMode
            />
          </div>
        );
      }

      // Step 5: Review & Send
      case 5: {
        const filledRecipients = recipients.filter((r) => r.name.trim());
        const placeholderEntries = Object.entries(placeholderValueMap).filter(
          ([, v]) => v != null && String(v).length > 0,
        );
        const placeholderLabel = (key: string) =>
          templatePlaceholders.find((p) => p.placeholderKey === key)?.displayLabel ?? key;
        const placeholderFieldType = (key: string) =>
          templatePlaceholders.find((p) => p.placeholderKey === key)?.fieldType;
        const formatDisplayValue = (key: string, val: string) => {
          if (placeholderFieldType(key) === 'DATE') {
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
            if (m) return `${m[3]}/${m[2]}/${m[1]}`;
          }
          return val;
        };
        const roleLabelMap: Record<string, string> = {
          SIGNER: t('K_DS_ROLE_SIGNER', 'Signer'),
          IN_PERSON_SIGNER: t('K_DS_ROLE_IN_PERSON_SIGNER', 'In-person signer'),
          CC: t('K_DS_ROLE_CC', 'CC'),
          APPROVER: t('K_DS_ROLE_APPROVER', 'Approver'),
        };
        const methodLabelMap: Record<string, string> = {
          ELECTRONIC: t('K_DS_METHOD_ELECTRONIC', 'Electronic'),
          WET_INK: t('K_DS_METHOD_WET_INK', 'Wet ink'),
        };
        return (
          <div className={styles.stepContent}>
            {/* Editable fields (last-chance edits) */}
            <div className={styles.reviewEditGroup}>
              <TextInput
                label={t('K_DS_LABEL_SUBJECT', 'Subject')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('K_DS_PLACEHOLDER_SUBJECT', 'e.g. Employment Agreement - John Doe')}
              />
              <div className={styles.formSection}>
                <label className={styles.label} htmlFor="wizard-message">
                  {t('K_DS_LABEL_MESSAGE_OPTIONAL', 'Message (optional)')}
                </label>
                <textarea
                  id="wizard-message"
                  className={styles.textarea}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t(
                    'K_DS_PLACEHOLDER_MESSAGE',
                    'Add a message for the recipients...',
                  )}
                />
              </div>
              <DatePicker
                label={t('K_DS_LABEL_EXPIRY_OPTIONAL', 'Expiry Date (optional)')}
                value={expiresAt}
                onChange={setExpiresAt}
              />
            </div>

            {/* Documents summary */}
            <div className={styles.reviewSection}>
              <h3 className={styles.reviewTitle}>
                {t('K_DS_LABEL_DOCUMENTS', 'Documents')} ({documents.length})
              </h3>
              <div className={styles.reviewList}>
                {documents.map((doc) => (
                  <div key={doc.documentOrder} className={styles.reviewItem}>
                    <span>{doc.name}</span>
                    <span className={styles.reviewMeta}>
                      {doc.pageCount > 0
                        ? t('K_DS_PAGES_COUNT', '{count} pages').replace(
                            '{count}',
                            String(doc.pageCount),
                          )
                        : ''}
                      {doc.sourceFileId ? ` · ${t('K_DS_FROM_TEMPLATE', 'From template')}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recipients summary */}
            <div className={styles.reviewSection}>
              <h3 className={styles.reviewTitle}>
                {t('K_DS_COLUMN_RECIPIENTS', 'Recipients')} ({filledRecipients.length})
              </h3>
              <div className={styles.reviewList}>
                {filledRecipients.map((r, i) => (
                  <div key={`${r.email}-${i}`} className={styles.reviewItem}>
                    <span>
                      <strong>{r.name}</strong>
                      {r.email ? ` — ${r.email}` : ''}
                    </span>
                    <span className={styles.reviewMeta}>
                      {roleLabelMap[r.role] ?? r.role} ·{' '}
                      {methodLabelMap[r.signingMethod] ?? r.signingMethod} ·{' '}
                      {t('K_DS_LABEL_ORDER', 'Order')}: {r.signingOrder}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const ccFilled = ccEmails.map((e) => e.trim()).filter(Boolean);
              const bccFilled = bccEmails.map((e) => e.trim()).filter(Boolean);
              if (ccFilled.length === 0 && bccFilled.length === 0) return null;
              return (
                <div className={styles.reviewSection}>
                  <h3 className={styles.reviewTitle}>
                    {t('K_DS_SECTION_OTHER_RECIPIENTS_TITLE', 'Other Recipients')}
                  </h3>
                  <div className={styles.reviewList}>
                    {ccFilled.length > 0 && (
                      <div className={styles.reviewItem}>
                        <span className={styles.reviewMeta}>
                          {t('K_DS_LABEL_CC', 'CC')}
                        </span>
                        <span>{ccFilled.join(', ')}</span>
                      </div>
                    )}
                    {bccFilled.length > 0 && (
                      <div className={styles.reviewItem}>
                        <span className={styles.reviewMeta}>
                          {t('K_DS_LABEL_BCC', 'BCC')}
                        </span>
                        <span>{bccFilled.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Placeholder values summary (only when any filled) */}
            {placeholderEntries.length > 0 && (
              <div className={styles.reviewSection}>
                <h3 className={styles.reviewTitle}>
                  {t('K_DS_LABEL_PLACEHOLDER_VALUES', 'Placeholder Values')} ({placeholderEntries.length})
                </h3>
                <div className={styles.reviewList}>
                  {placeholderEntries.map(([key, val]) => (
                    <div key={key} className={styles.reviewItem}>
                      <span className={styles.placeholderKey}>{placeholderLabel(key)}</span>
                      <span className={styles.reviewMeta}>{formatDisplayValue(key, val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inline document preview — placeholders resolved + fields placed.
                Same `decoratedFields` and `recipients` as Step 5 (Place Fields)
                so colors / signer labels reflect the actual mapping. */}
            {Object.keys(docPdfUrls).length > 0 && (
              <div className={styles.reviewSection}>
                <h3 className={styles.reviewTitle}>
                  {t('K_DS_REVIEW_PREVIEW_TITLE', 'Document Preview')}
                  {' '}({signingFields.length}{' '}
                  {t('K_DS_REVIEW_FIELDS_INLINE', 'fields')})
                </h3>
                <div className={styles.previewInline}>
                  <TemplateBuilder
                    templateFormat="PDF"
                    pdfUrl={docPdfUrls[activeDocumentOrder]}
                    pageCount={
                      documents.find((d) => d.documentOrder === activeDocumentOrder)
                        ?.pageCount || 1
                    }
                    documents={documents.map((d) => ({
                      name: d.name,
                      documentOrder: d.documentOrder,
                      sourceFileId: d.sourceFileId,
                      pageCount: d.pageCount,
                    }))}
                    docPdfUrls={docPdfUrls}
                    activeDocumentOrder={activeDocumentOrder}
                    onActiveDocumentChange={setActiveDocumentOrder}
                    initialFields={decoratedFields}
                    recipientCount={
                      recipients.filter(
                        (r) => r.role === 'SIGNER' || r.role === 'IN_PERSON_SIGNER',
                      ).length
                    }
                    recipients={recipients.map((r) => ({
                      name: r.name,
                      role: r.role,
                      signingOrder: r.signingOrder,
                    }))}
                    readOnly
                    disableAuthorMode
                  />
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Inline em-dash title with employee context — shown only when an employee
  // is loaded (Generate-Contract flow). The title stays single-line; the
  // context container truncates via ellipsis on narrow viewports so the title
  // itself never gets cut.
  const titleText = editRequestId
    ? t('K_DS_PAGE_TITLE_EDIT_SIGNING_REQUEST', 'Edit Signing Request')
    : t('K_DS_PAGE_TITLE_NEW_SIGNING_REQUEST', 'New Signing Request');
  const employeeCountryName = employee
    ? countries.find((c) => c.id === employee.countryId)?.name
    : undefined;
  const pageTitleNode = employee ? (
    <span className={styles.titleRow}>
      <span className={styles.titleText}>{titleText}</span>
      <span className={styles.titleSep}>—</span>
      <span className={styles.employeeContext}>
        <span className={styles.contextName}>
          {`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim()}
        </span>
        {employee.workEmail && (
          <>
            <span className={styles.contextBullet}>·</span>
            <span className={styles.contextEmail}>{employee.workEmail}</span>
          </>
        )}
        {employeeCountryName && (
          <>
            <span className={styles.contextBullet}>·</span>
            <span className={styles.contextChip}>{employeeCountryName}</span>
          </>
        )}
        {employeeEmploymentTypeName && (
          <>
            <span className={styles.contextBullet}>·</span>
            <span className={styles.contextChip}>{employeeEmploymentTypeName}</span>
          </>
        )}
      </span>
    </span>
  ) : (
    titleText
  );

  if (wizardForbidden) {
    return (
      <Forbidden
        variant="page"
        onAction={handleClose}
        actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
      />
    );
  }

  return (
    <FullPageStepper
      pageTitle={pageTitleNode}
      steps={steps}
      activeStep={currentStep}
      onClose={handleClose}
      onStepClick={goToStep}
      contentMaxWidth={currentStep === 4 ? '100%' : '70%'}
      contentVariant={currentStep === 4 ? 'fullHeight' : 'default'}
      footer={
        <StepperModalFooter
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          onNext={handleNext}
          onPrev={handleStepperBack}
          onCancel={handleClose}
          isSubmitting={isAdvancing || createMutation.isPending || sendMutation.isPending}
          submitLabel={t('K_DS_BUTTON_SEND', 'Send Signing Request')}
          disableNext={currentStep === 4 && !placeFieldsPreviewReady}
        />
      }
    >
      {renderStepContent()}
      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onConfirm={handleTemplatesPicked}
        employeeContext={employeeContext}
        excludeIds={documents
          .map((d) => d.sourceTemplateId ?? null)
          .filter((v): v is string => !!v)}
      />
      {showPreviewModal && (
        <Modal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title={t('K_DS_MODAL_PREVIEW_DOCUMENTS_TITLE', 'Document Preview')}
          size="xl"
        >
          <div className={styles.previewModalBody}>
            <TemplateBuilder
              templateFormat="PDF"
              pdfUrl={docPdfUrls[activeDocumentOrder]}
              pageCount={
                documents.find((d) => d.documentOrder === activeDocumentOrder)?.pageCount || 1
              }
              documents={documents.map((d) => ({
                name: d.name,
                documentOrder: d.documentOrder,
                sourceFileId: d.sourceFileId,
                pageCount: d.pageCount,
              }))}
              docPdfUrls={docPdfUrls}
              activeDocumentOrder={activeDocumentOrder}
              onActiveDocumentChange={setActiveDocumentOrder}
              initialFields={previewFields}
              recipientCount={previewRecipients.length}
              recipients={previewRecipients}
              readOnly
              disableAuthorMode
            />
          </div>
        </Modal>
      )}
      {showReviewModal && createdRequestId && (
        <SendForReviewModal
          targetType="ENVELOPE"
          targetId={createdRequestId}
          initialReviewers={initialReviewers}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => {
            setShowReviewModal(false);
            addNotification({
              variant: 'success',
              title: t('K_DS_NOTIF_REVIEW_SENT', 'Sent for review'),
            });
            // Signing request is now PENDING_REVIEW — nothing left to do in
            // the wizard. Invalidate list + detail caches and redirect to the
            // detail page so the author can track review progress.
            void queryClient.invalidateQueries({ queryKey: ['dsEnvelopes'] });
            void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', createdRequestId] });
            void queryClient.invalidateQueries({
              queryKey: ['ds-reviews', 'ENVELOPE', createdRequestId],
            });
            navigate({
              to: '/w/signing-requests/$requestId',
              params: { requestId: createdRequestId },
            });
          }}
        />
      )}
      <Modal
        isOpen={showRevertConfirm}
        onClose={() => {
          if (revertMutation.isPending) return;
          setShowRevertConfirm(false);
        }}
        title={t('K_DS_REVERT_REVIEW_COMPLETED_TITLE', 'Reset to draft?')}
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setShowRevertConfirm(false)}
              disabled={revertMutation.isPending}
            >
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button
              variant="filled"
              loading={revertMutation.isPending}
              onClick={() => revertMutation.mutate()}
            >
              {t('K_DS_REVERT_REVIEW_COMPLETED_CONFIRM', 'Reset to draft')}
            </Button>
          </>
        }
      >
        <p>
          {t(
            'K_DS_REVERT_REVIEW_COMPLETED_BODY',
            "Going back will reset this signing request to DRAFT and discard the completed review's lock. Continue?",
          )}
        </p>
      </Modal>
    </FullPageStepper>
  );
}
